import { forwardRef, useCallback, useEffect, useImperativeHandle, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  IconAlertTriangle,
  IconCheck,
  IconChevronLeft,
  IconChevronRight,
  IconClock,
  IconCloudCheck,
  IconDots,
  IconDownload,
  IconFolder,
  IconHome,
  IconLoader2,
  IconMaximize,
  IconMinus,
  IconPlayerPlay,
  IconPlus,
  IconPhoto,
  IconTrash,
  IconWand,
  IconX,
} from "@tabler/icons-react";
import { AdvancedBeautifyPage } from "./advanced-beautify";
import { ApiError, getUserAuthToken, userApi } from "./api";
import { clientId } from "./client-id";
import { debugFrontendEvent } from "./development-debug-log";
import { GenerationModeTabs } from "./generation-mode-tabs";
import { projectFailureMessage, projectHasVisibleFailure } from "./project-failure";
import { projectGenerationPath } from "./project-routes";
import { allowedPptxExportModes, exportOrDownloadPptx, type PptxExportMode, type PptxExportProgress, waitForExportDialogPaint } from "./pptx-export";
import { PptxExportChoiceDialog } from "./pptx-export-dialog";
import type {
  ProjectDetailResponse,
  ProjectProgressDeckPreview,
  ProjectRender,
  ProjectSourceContentPreview,
  ProjectSourceContentPreviewRef,
} from "./project-types";
import { notifyCreditActionSubmitted, requestCreditSpend } from "./credit-interactions";
import { useUserAuth } from "./auth";

type GenerationPhase = "starting" | "outline" | "rendering" | "complete" | "failed" | "cancelled";
type RailSlideStatus = "complete" | "generating" | "waiting" | "failed" | "cancelled";
const ADVANCED_BEAUTIFY_PRODUCT_CODE = "advanced-beautify-image2";
const MIN_PROGRESS_DECK_REFRESH_TIMEOUT_MS = 12_000;
const MAX_PROGRESS_DECK_REFRESH_TIMEOUT_MS = 45_000;
const SOURCE_CONTENT_STREAM_INTERVAL_MS = 32;

interface RailSlide {
  index: number;
  title: string;
  subtitle?: string;
  status: RailSlideStatus;
  thumbnailUrl?: string;
  kind?: "generated" | "blank" | "theme-layout";
}

interface GenerationViewState {
  phase: GenerationPhase;
  percent: number;
  totalSlides: number;
  completedSlides: number;
  currentSlide: number;
  headline: string;
  subline: string;
  detailText: string;
}

interface DeckFrameHandle {
  goToSlide: (slide: number) => void;
  captureProjectCover: () => Promise<ProjectCoverCaptureResult>;
}

interface ProjectCoverCaptureResult {
  blob: Blob;
  slideIndex: 1;
  width: 640;
  height: 360;
  mimeType: "image/webp" | "image/jpeg" | "image/png";
  extension: "webp" | "jpg" | "png";
}

export function StandardGeneratingPage() {
  const { taskId = "" } = useParams();
  const [searchParams] = useSearchParams();
  const advancedActive = searchParams.get("mode") === "advanced";
  const visitedStandardTasks = useRef(new Set<string>());
  const visitedAdvancedTasks = useRef(new Set<string>());
  if (advancedActive) visitedAdvancedTasks.current.add(taskId);
  else visitedStandardTasks.current.add(taskId);

  return <>
    {visitedStandardTasks.current.has(taskId) && <div className="generation-mode-surface" hidden={advancedActive}>
      <StandardGeneratingModePage key={`standard:${taskId}`}/>
    </div>}
    {visitedAdvancedTasks.current.has(taskId) && <div className="generation-mode-surface" hidden={!advancedActive}>
      <AdvancedBeautifyPage key={`advanced:${taskId}`}/>
    </div>}
  </>;
}

function StandardGeneratingModePage() {
  const { taskId = "" } = useParams();
  const navigate = useNavigate();
  const auth = useUserAuth();
  const isChinaEdition = auth.me?.features?.edition === "china";
  const exportModes = allowedPptxExportModes(isChinaEdition);
  const showAdvancedBeautify = !isChinaEdition;
  const [detail, setDetail] = useState<ProjectDetailResponse | undefined>(() => loadSessionDetail(taskId));
  const [error, setError] = useState("");
  const [requestId, setRequestId] = useState("");
  const [refreshNotice, setRefreshNotice] = useState("");
  const [refreshRequestId, setRefreshRequestId] = useState("");
  const [sourceContentPreview, setSourceContentPreview] = useState<ProjectSourceContentPreview>();
  const [sourcePreviewRetryToken, setSourcePreviewRetryToken] = useState(0);
  const [busy, setBusy] = useState("");
  const [menuOpen, setMenuOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [retryOpen, setRetryOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [exportMode, setExportMode] = useState<PptxExportMode>();
  const [exportProgress, setExportProgress] = useState<PptxExportProgress>();
  const [exportError, setExportError] = useState("");
  const [slideAdditionOpen, setSlideAdditionOpen] = useState(false);
  const [slideAdditionText, setSlideAdditionText] = useState("");
  const [slideAdditionFiles, setSlideAdditionFiles] = useState<File[]>([]);
  const [slideAdditionError, setSlideAdditionError] = useState("");
  const [slideAdditionSubmitting, setSlideAdditionSubmitting] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(1);
  const [followLatestProgressSlide, setFollowLatestProgressSlide] = useState(true);
  const [sseConnected, setSseConnected] = useState(false);
  const [zoom, setZoom] = useState(100);
  const [finalFrameReady, setFinalFrameReady] = useState(false);
  const [runtimeSlideCount, setRuntimeSlideCount] = useState(0);
  const [deckDirty, setDeckDirty] = useState(false);
  const [projectCoverRetryToken, setProjectCoverRetryToken] = useState(0);
  const [progressCoverReadyRevision, setProgressCoverReadyRevision] = useState("");
  const [lastGoodProgressPreview, setLastGoodProgressPreview] = useState<ProjectProgressDeckPreview | undefined>(
    () => isUsableStandardProgressPreview(detail, detail?.generationProgressDeckPreview) ? detail?.generationProgressDeckPreview || undefined : undefined,
  );
  const finalFrame = useRef<HTMLIFrameElement>(null);
  const progressFrame = useRef<DeckFrameHandle>(null);
  const stageRef = useRef<HTMLElement>(null);
  const slideAdditionDialog = useRef<HTMLFormElement>(null);
  const zoomRef = useRef(zoom);
  const pendingDeckStateSave = useRef<{ renderId: string; state: Record<string, unknown> } | null>(null);
  const deckStateSaveTimer = useRef<number | null>(null);
  const deckStateSaveInFlight = useRef<Promise<boolean> | null>(null);
  const postFinalViewportZoom = useCallback((nextZoom: number, resetPan = false) => {
    const frameWindow = finalFrame.current?.contentWindow as (Window & {
      __setDFAIViewportZoom?: (zoom: number, resetPan: boolean) => void;
    }) | null;
    if (!frameWindow) return;
    const message = {
      type: "df-html-ppt-set-viewport-zoom",
      zoom: nextZoom,
      resetPan,
    };
    try {
      frameWindow.postMessage(message, "*");
    } catch {
      // The same-origin bridge call below remains available for local preview URLs.
    }
    try {
      frameWindow.__setDFAIViewportZoom?.(nextZoom, resetPan);
    } catch {
      // Cross-origin preview URLs continue to use the postMessage path above.
    }
  }, []);
  const postFinalAiGeneratedLabel = useCallback((visible: boolean) => {
    const frameWindow = finalFrame.current?.contentWindow as (Window & {
      __setDFAIAiGeneratedLabel?: (visible: boolean) => void;
    }) | null;
    if (!frameWindow) return;
    const message = { type: "df-html-ppt-set-ai-generated-label", visible };
    try {
      frameWindow.postMessage(message, "*");
    } catch {
      // The same-origin bridge call below remains available for local preview URLs.
    }
    try {
      frameWindow.__setDFAIAiGeneratedLabel?.(visible);
    } catch {
      // Cross-origin preview URLs continue to use the postMessage path above.
    }
    try {
      const pager = finalFrame.current?.contentDocument?.getElementById("deck-page-pager");
      if (!pager) return;
      let label = pager.querySelector<HTMLElement>("#dfai-ai-generated-label")
        || finalFrame.current?.contentDocument?.getElementById("dfai-ai-generated-label");
      if (!label && !visible) return;
      if (!label) {
        label = document.createElement("span");
        label.id = "dfai-ai-generated-label";
      }
      label.setAttribute("aria-label", "包含AI生成标识");
      label.textContent = "包含AI生成";
      label.style.cssText = "position:absolute;left:50%;bottom:calc(100% + 8px);z-index:1;padding:6px 9px;border:1px solid rgba(103,71,239,.2);border-radius:999px;color:#715bd0;background:rgba(245,242,255,.86);box-shadow:0 6px 18px rgba(86,34,255,.12);backdrop-filter:blur(8px);font:700 10px/1 Arial,'Microsoft YaHei',sans-serif;letter-spacing:.04em;white-space:nowrap;pointer-events:none;user-select:none;transform:translateX(-50%);";
      if (label.parentElement !== pager) pager.appendChild(label);
      label.hidden = !visible;
    } catch {
      // Cross-origin preview URLs rely on the bridge's postMessage handler.
    }
  }, []);
  useEffect(() => {
    zoomRef.current = zoom;
  }, [zoom]);
  const detailRef = useRef(detail);
  const taskIdRef = useRef(taskId);
  const refreshRef = useRef<TaskDetailRefreshState>({ inFlight: false, consecutivePollFailures: 0 });
  const railDebugKeyRef = useRef("");
  const sourcePreviewLoadedKeyRef = useRef("");
  const sourcePreviewInFlightKeyRef = useRef("");
  const sourcePreviewAttemptsRef = useRef(new Map<string, number>());
  const projectCoverAttemptsRef = useRef(new Map<string, number>());
  const uploadedProgressCoverAttemptsRef = useRef(new Set<string>());
  const closeSlideAddition = useCallback(() => {
    setSlideAdditionOpen(false);
    window.setTimeout(() => finalFrame.current?.focus(), 0);
  }, []);

  useEffect(() => {
    if (detail?.task.generationMode !== "image") return;
    navigate(projectGenerationPath(taskId, "image"), { replace: true });
  }, [detail?.task.generationMode, navigate, taskId]);

  const load = useCallback(async (silent = false, isPoll = false) => {
    const refresh = refreshRef.current;
    const trigger = isPoll ? "poll_or_sse" : silent ? "silent" : "initial";
    if (!beginTaskDetailRefresh(refresh)) {
      debugFrontendEvent("standard_generation_detail_refresh_skipped", {
        taskId,
        trigger,
        reason: "in_flight",
        current: generationDetailDebugState(detailRef.current),
      });
      return;
    }
    const startedAt = performance.now();
    debugFrontendEvent("standard_generation_detail_refresh_started", {
      taskId,
      trigger,
      current: generationDetailDebugState(detailRef.current),
    });
    try {
      const data = await userApi<ProjectDetailResponse>(`/api/app/v2/ppt-tasks/${encodeURIComponent(taskId)}/generation-view`, {
        cache: "no-store",
        headers: { "cache-control": "no-cache" },
      });
      const taskChanged = taskIdRef.current !== taskId;
      const shouldApply = !taskChanged && shouldApplyTaskDetail(detailRef.current, data);
      debugFrontendEvent("standard_generation_detail_refresh_received", {
        taskId,
        trigger,
        outcome: taskChanged ? "rejected_task_changed" : shouldApply ? "applied" : "rejected_stale",
        durationMs: Math.round((performance.now() - startedAt) * 10) / 10,
        current: generationDetailDebugState(detailRef.current),
        incoming: generationDetailDebugState(data),
      });
      if (!shouldApply) return;
      detailRef.current = data;
      setDetail(data);
      sessionStorage.setItem(`pptdeep:task:${taskId}`, JSON.stringify(data));
      resetTaskDetailPollFailures(refresh);
      setRefreshNotice("");
      setRefreshRequestId("");
      if (!silent) setError("");
    } catch (caught) {
      if (taskIdRef.current !== taskId) return;
      debugFrontendEvent("standard_generation_detail_refresh_failed", {
        taskId,
        trigger,
        durationMs: Math.round((performance.now() - startedAt) * 10) / 10,
        errorCode: caught instanceof ApiError ? caught.code : undefined,
        requestId: caught instanceof ApiError ? caught.requestId : undefined,
        error: caught instanceof Error ? caught.message : String(caught),
      });
      if (isPoll) {
        if (recordTaskDetailPollFailure(refresh)) {
          setRefreshNotice("状态暂未刷新，正在重试。");
          setRefreshRequestId(caught instanceof ApiError ? caught.requestId || "" : "");
        }
      } else if (!silent) {
        setError(caught instanceof Error ? caught.message : "项目加载失败");
        setRequestId(caught instanceof ApiError ? caught.requestId || "" : "");
      }
    } finally {
      finishTaskDetailRefresh(refresh);
    }
  }, [taskId]);

  const finalRender = useMemo(() => pickFinalRender(detail), [detail]);
  useEffect(() => {
    if (finalRender?.renderer !== "dfai" || !finalRender.previewUrl) return;
    postFinalAiGeneratedLabel(isChinaEdition);
  }, [finalRender?.previewUrl, finalRender?.renderer, isChinaEdition, postFinalAiGeneratedLabel]);
  const slideAdditionActive = ["queued", "running"].includes(detail?.slideAddition?.status || "");
  const standardActive = hasActiveStandardGeneration(detail);
  const retryAvailable = canRetryStandardGeneration(detail, finalRender);
  const shouldPoll = shouldPollStandardDetail(detail, finalRender);
  const detailPollIntervalMs = sseConnected ? 3000 : 1000;
  const advancedEnabled = Boolean(finalRender?.renderer === "dfai");
  const openSlideAddition = useCallback(() => {
    if (slideAdditionActive) return;
    setSlideAdditionError("");
    setSlideAdditionText("");
    setSlideAdditionFiles([]);
    setSlideAdditionOpen(true);
  }, [slideAdditionActive]);

  const persistDeckState = useCallback(async (
    renderId: string,
    state: Record<string, unknown>,
  ): Promise<boolean> => {
    try {
      await userApi(`/api/app/v2/renders/${encodeURIComponent(renderId)}/deck-state`, {
        method: "POST",
        body: JSON.stringify({ state }),
      });
      finalFrame.current?.contentWindow?.postMessage({ type: "df-html-ppt-deck-state-save-result", ok: true }, "*");
      return true;
    } catch (caught) {
      const message = caught instanceof Error ? caught.message : "新增幻灯片保存失败";
      setError(message);
      setRequestId(caught instanceof ApiError ? caught.requestId || "" : "");
      finalFrame.current?.contentWindow?.postMessage({
        type: "df-html-ppt-deck-state-save-result",
        ok: false,
        error: message,
      }, "*");
      return false;
    }
  }, []);

  const flushDeckStateSave = useCallback(async (): Promise<boolean> => {
    if (deckStateSaveTimer.current !== null) {
      window.clearTimeout(deckStateSaveTimer.current);
      deckStateSaveTimer.current = null;
    }
    let saved = true;
    while (deckStateSaveInFlight.current || pendingDeckStateSave.current) {
      if (deckStateSaveInFlight.current) {
        saved = (await deckStateSaveInFlight.current) && saved;
        continue;
      }
      const pending = pendingDeckStateSave.current;
      if (!pending) break;
      pendingDeckStateSave.current = null;
      const request = persistDeckState(pending.renderId, pending.state);
      deckStateSaveInFlight.current = request;
      try {
        saved = (await request) && saved;
      } finally {
        if (deckStateSaveInFlight.current === request) deckStateSaveInFlight.current = null;
      }
    }
    return saved;
  }, [persistDeckState]);

  const scheduleDeckStateSave = useCallback((renderId: string, state: Record<string, unknown>) => {
    pendingDeckStateSave.current = { renderId, state };
    if (deckStateSaveTimer.current !== null) window.clearTimeout(deckStateSaveTimer.current);
    deckStateSaveTimer.current = window.setTimeout(() => {
      deckStateSaveTimer.current = null;
      void flushDeckStateSave();
    }, 350);
  }, [flushDeckStateSave]);

  useEffect(() => {
    if (!retryAvailable) {
      setRetryOpen(false);
      return;
    }
    if (!retryOpen) return;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setRetryOpen(false);
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [retryAvailable, retryOpen]);

  useEffect(() => {
    if (!detail) return;
    debugFrontendEvent("standard_generation_state", {
      taskId,
      status: detail.task.status,
      stage: detail.task.currentStage,
      progressPercent: detail.task.progressPercent,
      progressText: detail.task.progressText,
      currentRenderId: detail.task.currentRenderId,
      finalRenderId: finalRender?.id,
      finalRenderStatus: finalRender?.status,
      slideCount: finalRender?.outputManifest?.slides.length || detail.generationProgressDeckPreview?.totalSlides,
    });
  }, [detail, finalRender, taskId]);

  useEffect(() => {
    taskIdRef.current = taskId;
    const cachedDetail = loadSessionDetail(taskId);
    detailRef.current = cachedDetail;
    refreshRef.current = { inFlight: false, consecutivePollFailures: 0 };
    setDetail(cachedDetail);
    setLastGoodProgressPreview(isUsableStandardProgressPreview(cachedDetail, cachedDetail?.generationProgressDeckPreview)
      ? cachedDetail?.generationProgressDeckPreview || undefined
      : undefined);
    setCurrentSlide(1);
    setFollowLatestProgressSlide(true);
    setError("");
    setRequestId("");
    setRefreshNotice("");
    setRefreshRequestId("");
    setSourceContentPreview(undefined);
    setSourcePreviewRetryToken(0);
    sourcePreviewLoadedKeyRef.current = "";
    sourcePreviewInFlightKeyRef.current = "";
    sourcePreviewAttemptsRef.current.clear();
    projectCoverAttemptsRef.current.clear();
    uploadedProgressCoverAttemptsRef.current.clear();
    setProgressCoverReadyRevision("");
    setRetryOpen(false);
    setSseConnected(false);
    setRuntimeSlideCount(0);
    setDeckDirty(false);
    setSlideAdditionOpen(false);
    setSlideAdditionText("");
    setSlideAdditionFiles([]);
    setSlideAdditionError("");
    setSlideAdditionSubmitting(false);
    pendingDeckStateSave.current = null;
    if (deckStateSaveTimer.current !== null) {
      window.clearTimeout(deckStateSaveTimer.current);
      deckStateSaveTimer.current = null;
    }
  }, [taskId]);
  useEffect(() => () => {
    if (deckStateSaveTimer.current !== null) window.clearTimeout(deckStateSaveTimer.current);
  }, []);

  useEffect(() => {
    if (!slideAdditionOpen || slideAdditionSubmitting) return;
    const manageDialogKeyboard = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        closeSlideAddition();
        return;
      }
      if (event.key !== "Tab") return;
      const controls = [...(slideAdditionDialog.current?.querySelectorAll<HTMLElement>(
        "button:not(:disabled), textarea:not(:disabled), input:not(:disabled), [tabindex]:not([tabindex='-1'])",
      ) || [])].filter((element) => element.offsetParent !== null);
      if (!controls.length) return;
      const first = controls[0];
      const last = controls[controls.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    };
    window.addEventListener("keydown", manageDialogKeyboard);
    return () => window.removeEventListener("keydown", manageDialogKeyboard);
  }, [closeSlideAddition, slideAdditionOpen, slideAdditionSubmitting]);

  useEffect(() => { void load(Boolean(detail)); }, [load]);
  useEffect(() => {
    if (!shouldPoll) return;
    const timer = window.setInterval(() => void load(true, true), detailPollIntervalMs);
    return () => window.clearInterval(timer);
  }, [detailPollIntervalMs, load, shouldPoll]);
  useEffect(() => {
    if (!shouldPoll) return;
    return subscribeStandardGenerationEvents(taskId, () => void load(true, true), setSseConnected);
  }, [load, shouldPoll, taskId]);

  const view = useMemo(() => deriveGenerationView(detail, finalRender), [detail, finalRender]);
  const modelStreamActive = shouldRunStandardModelStream(detail?.task.status, view.phase);
  const [assistantCollapsed, setAssistantCollapsed] = useState(() => standardAssistantDefaultCollapsed(view.phase));
  const assistantPhaseRef = useRef(view.phase);
  useEffect(() => {
    if (assistantPhaseRef.current === view.phase) return;
    assistantPhaseRef.current = view.phase;
    setAssistantCollapsed(standardAssistantDefaultCollapsed(view.phase));
  }, [view.phase]);
  const effectiveTotalSlides = finalRender ? Math.max(view.totalSlides, runtimeSlideCount) : view.totalSlides;
  const availableSlides = finalRender
    ? effectiveTotalSlides
    : Math.max(1, visibleProgressSlideCount(detail, lastGoodProgressPreview));
  const activeSlide = Math.max(1, Math.min(currentSlide, Math.max(1, effectiveTotalSlides)));
  const railSlides = useMemo(() => extendRuntimeRailSlides(
    buildRailSlides(detail, finalRender, view.completedSlides, view.currentSlide, view.phase),
    effectiveTotalSlides,
  ), [detail, effectiveTotalSlides, finalRender, view.completedSlides, view.currentSlide, view.phase]);
  useEffect(() => {
    const preview = detail?.generationProgressDeckPreview;
    const state = {
      taskId,
      taskStatus: detail?.task.status,
      taskUpdatedAt: detail?.task.updatedAt,
      attemptNo: preview?.attemptNo,
      revision: preview?.revision,
      reportedCompletedSlides: preview?.completedSlides,
      derivedCompletedSlides: view.completedSlides,
      totalSlides: view.totalSlides,
      generatingSlide: view.currentSlide,
      selectedSlide: currentSlide,
      followLatestProgressSlide,
      completeSlideIndexes: railSlides.filter((slide) => slide.status === "complete").map((slide) => slide.index),
      generatingSlideIndexes: railSlides.filter((slide) => slide.status === "generating").map((slide) => slide.index),
      waitingSlideIndexes: railSlides.filter((slide) => slide.status === "waiting").map((slide) => slide.index),
      failedSlideIndexes: railSlides.filter((slide) => slide.status === "failed").map((slide) => slide.index),
      railItems: railSlides.map((slide) => ({
        index: slide.index,
        title: slide.title,
        subtitle: slide.subtitle,
        status: slide.status,
        kind: slide.kind,
        thumbnailUrl: slide.thumbnailUrl,
        hasThumbnail: Boolean(slide.thumbnailUrl),
        selected: slide.index === activeSlide,
        placeholderText: slide.thumbnailUrl
          ? undefined
          : slide.status === "generating"
            ? "生成中"
            : slide.status === "failed"
              ? "生成失败"
              : slide.status === "cancelled"
                ? "已取消"
                : slide.status === "waiting"
                  ? (["failed", "cancelled"].includes(view.phase) ? "未生成" : "待生成")
                  : slide.status === "complete"
                    ? "已生成"
                    : undefined,
      })),
      finalRenderId: finalRender?.id,
    };
    const debugKey = JSON.stringify(state);
    if (railDebugKeyRef.current === debugKey) return;
    railDebugKeyRef.current = debugKey;
    debugFrontendEvent("standard_generation_rail_state", state);
  }, [activeSlide, currentSlide, detail, finalRender?.id, followLatestProgressSlide, railSlides, taskId, view.completedSlides, view.currentSlide, view.phase, view.totalSlides]);
  const railView = finalRender && effectiveTotalSlides !== view.totalSlides
    ? { ...view, totalSlides: effectiveTotalSlides, completedSlides: effectiveTotalSlides, currentSlide: activeSlide }
    : view;
  const outlineSlides = detail?.planningPreview?.snapshot.slides || [];
  const progressPreview = detail?.generationProgressDeckPreview;
  useEffect(() => {
    if (isUsableStandardProgressPreview(detail, progressPreview)) setLastGoodProgressPreview(progressPreview || undefined);
  }, [detail, progressPreview]);
  const visibleProgressPreview = isUsableStandardProgressPreview(detail, progressPreview)
    ? progressPreview
    : isProgressPreviewForCurrentAttempt(detail, lastGoodProgressPreview) ? lastGoodProgressPreview : undefined;
  const hasProgressDeck = Boolean(visibleProgressPreview?.previewUrl);
  const hasFinalDeck = Boolean(finalRender?.previewUrl);
  const hasDeckFrame = hasFinalDeck || hasProgressDeck;

  useEffect(() => {
    const preview = visibleProgressPreview;
    if (!canCaptureProgressProjectCover(preview, progressCoverReadyRevision)) return;
    const captureRevision = progressProjectCoverRevision(preview);
    const attemptKey = `${taskId}:${Number(preview.attemptNo || 0)}`;
    if (uploadedProgressCoverAttemptsRef.current.has(attemptKey)) return;
    const captureKey = `progress:${taskId}:${captureRevision}`;
    const attempts = projectCoverAttemptsRef.current.get(captureKey) || 0;
    if (attempts >= 3) return;
    projectCoverAttemptsRef.current.set(captureKey, attempts + 1);
    let cancelled = false;
    let retryTimer: number | undefined;
    const capture = async () => {
      try {
        const result = await progressFrame.current?.captureProjectCover();
        if (!result) throw new Error("第一页增量预览尚未提供项目封面截图能力");
        if (cancelled) return;
        if (result.slideIndex !== 1
          || result.width !== 640
          || result.height !== 360
          || !["image/webp", "image/jpeg", "image/png"].includes(result.mimeType)
          || result.blob.type !== result.mimeType) {
          throw new Error("第一页增量预览截图结果不符合 640×360 图片约束");
        }
        const form = new FormData();
        form.append("request", JSON.stringify({ slideIndex: 1, captureRevision }));
        form.append("cover", result.blob, `01.${result.extension}`);
        await userApi(`/api/app/v2/ppt-tasks/${encodeURIComponent(taskId)}/project-cover`, {
          method: "POST",
          body: form,
        });
        if (!cancelled) {
          uploadedProgressCoverAttemptsRef.current.add(attemptKey);
          await load(true, true);
        }
      } catch (caught) {
        debugFrontendEvent("progress_project_cover_capture_failed", {
          taskId,
          attemptNo: preview.attemptNo,
          revision: preview.revision,
          captureRevision,
          attempt: attempts + 1,
          error: caught instanceof Error ? caught.message : String(caught),
        });
        if (!cancelled && attempts + 1 < 3) {
          retryTimer = window.setTimeout(() => setProjectCoverRetryToken((value) => value + 1), 1_500 * (attempts + 1));
        }
      }
    };
    const startTimer = window.setTimeout(() => void capture(), attempts === 0 ? 100 : 1_000);
    return () => {
      cancelled = true;
      window.clearTimeout(startTimer);
      if (retryTimer) window.clearTimeout(retryTimer);
    };
  }, [load, progressCoverReadyRevision, projectCoverRetryToken, taskId, visibleProgressPreview]);
  const centerContentMode = standardCenterContentMode({
    hasDeckFrame,
    hasPlanningPreview: Boolean(detail?.planningPreview),
    hasSourceContentPreview: Boolean(sourceContentPreview),
  });
  const showOutlineInHero = centerContentMode === "outline";
  const outlinePreviewKey = detail?.planningPreview ? planningPreviewIdentity(detail.planningPreview) : "";
  const sourcePreviewRef = detail?.sourceContentPreviewRef;
  const sourcePreviewRefKey = sourcePreviewRef ? sourceContentPreviewIdentity(sourcePreviewRef) : "";
  useEffect(() => {
    if (sourcePreviewRefKey) return;
    sourcePreviewLoadedKeyRef.current = "";
    setSourceContentPreview(undefined);
  }, [sourcePreviewRefKey]);
  useEffect(() => {
    if (!sourcePreviewRef || !sourcePreviewRefKey || detail?.planningPreview || hasDeckFrame) return;
    if (sourcePreviewLoadedKeyRef.current === sourcePreviewRefKey && sourceContentPreview) return;
    if (sourcePreviewLoadedKeyRef.current && sourcePreviewLoadedKeyRef.current !== sourcePreviewRefKey) {
      sourcePreviewLoadedKeyRef.current = "";
      setSourceContentPreview(undefined);
    }
    const cached = loadSourceContentPreview(taskId, sourcePreviewRefKey);
    if (cached) {
      sourcePreviewLoadedKeyRef.current = sourcePreviewRefKey;
      setSourceContentPreview(cached);
      return;
    }
    if (sourcePreviewInFlightKeyRef.current === sourcePreviewRefKey) return;
    const attempts = sourcePreviewAttemptsRef.current.get(sourcePreviewRefKey) || 0;
    if (attempts >= 3) return;
    sourcePreviewInFlightKeyRef.current = sourcePreviewRefKey;
    sourcePreviewAttemptsRef.current.set(sourcePreviewRefKey, attempts + 1);
    let active = true;
    let retryTimer: number | undefined;
    void userApi<{ ok: true; preview: ProjectSourceContentPreview }>(
      `/api/app/v2/ppt-tasks/${encodeURIComponent(taskId)}/source-content-preview/${encodeURIComponent(sourcePreviewRef.id)}`,
      { cache: "no-store", headers: { "cache-control": "no-cache" } },
    ).then((response) => {
      storeSourceContentPreview(taskId, sourcePreviewRefKey, response.preview);
      sourcePreviewLoadedKeyRef.current = sourcePreviewRefKey;
      if (active) setSourceContentPreview(response.preview);
    }).catch(() => {
      if (active && attempts + 1 < 3) {
        retryTimer = window.setTimeout(() => setSourcePreviewRetryToken((value) => value + 1), 3000);
      }
    }).finally(() => {
      if (sourcePreviewInFlightKeyRef.current === sourcePreviewRefKey) sourcePreviewInFlightKeyRef.current = "";
    });
    return () => {
      active = false;
      if (retryTimer) window.clearTimeout(retryTimer);
    };
  }, [detail?.planningPreview, hasDeckFrame, sourceContentPreview, sourcePreviewRefKey, sourcePreviewRetryToken, taskId]);
  const showSourceContentInHero = centerContentMode === "source"
    && !["failed", "cancelled", "complete"].includes(view.phase);
  const showProgressOnly = !hasDeckFrame;
  const showWaitingContent = showOutlineInHero || showSourceContentInHero;
  const showExpandedOutline = showWaitingContent && standardActive;

  useEffect(() => {
    setCurrentSlide((previous) => Math.max(1, Math.min(previous, Math.max(1, effectiveTotalSlides))));
  }, [effectiveTotalSlides]);

  useEffect(() => {
    if (finalRender || !followLatestProgressSlide) return;
    const target = Math.max(1, Math.min(availableSlides, view.totalSlides || availableSlides));
    setCurrentSlide((previous) => previous === target ? previous : target);
    progressFrame.current?.goToSlide(target);
  }, [availableSlides, finalRender, followLatestProgressSlide, view.totalSlides]);

  const handleProgressSlideChange = useCallback((slide: number) => {
    setFollowLatestProgressSlide(false);
    setCurrentSlide((previous) => previous === slide ? previous : slide);
  }, []);

  useEffect(() => {
    setFinalFrameReady(false);
    setRuntimeSlideCount(0);
    setDeckDirty(false);
    setZoom(100);
  }, [finalRender?.id, finalRender?.previewUrl, taskId]);

  useEffect(() => {
    const receive = (event: MessageEvent) => {
      if (event.source !== finalFrame.current?.contentWindow) return;
      const data = event.data as {
        type?: string;
        slide?: unknown;
        slideCount?: unknown;
        dirty?: unknown;
        error?: unknown;
        runtimeVersion?: unknown;
        capabilities?: {
          addThemeLayoutSlide?: unknown;
          addBlankSlide?: unknown;
          persistedDeckState?: unknown;
        };
        state?: unknown;
      };
      const reportedSlideCount = Math.max(0, Number(data.slideCount) || 0);
      if (data?.type === "df-html-ppt-ready") {
        setFinalFrameReady(true);
        if (reportedSlideCount) setRuntimeSlideCount(reportedSlideCount);
        if (finalRender?.renderer === "dfai") {
          postFinalViewportZoom(zoomRef.current, true);
        }
      } else if (data?.type === "df-html-ppt-slide-change") {
        setCurrentSlide(Math.max(1, Number(data.slide) || 1));
        if (reportedSlideCount) setRuntimeSlideCount(reportedSlideCount);
      } else if (data?.type === "df-html-ppt-slide-catalog-change") {
        if (reportedSlideCount) setRuntimeSlideCount(reportedSlideCount);
        setCurrentSlide(Math.max(1, Number(data.slide) || 1));
        if (data.dirty) {
          setDeckDirty(true);
          if (finalRender?.renderer === "dfai"
            && data.state
            && typeof data.state === "object"
            && !Array.isArray(data.state)) {
            scheduleDeckStateSave(finalRender.id, data.state as Record<string, unknown>);
          }
        }
      } else if (data?.type === "df-html-ppt-request-add-generated-slide") {
        if (slideAdditionActive) {
          finalFrame.current?.contentWindow?.postMessage({
            type: "df-html-ppt-slide-addition-state",
            status: detail?.slideAddition?.status,
            message: detail?.slideAddition?.progressText || "新增幻灯片正在生成",
          }, "*");
          return;
        }
        openSlideAddition();
      } else if (data?.type === "df-html-ppt-add-theme-slide-failed"
        || data?.type === "df-html-ppt-add-blank-slide-failed") {
        setError(String(data.error || "新增幻灯片失败"));
      }
    };
    window.addEventListener("message", receive);
    return () => window.removeEventListener("message", receive);
  }, [detail?.slideAddition?.progressText, detail?.slideAddition?.status, finalRender?.id, finalRender?.renderer, openSlideAddition, postFinalViewportZoom, scheduleDeckStateSave, slideAdditionActive]);

  useEffect(() => {
    const manifestId = finalRender?.outputManifestId;
    if (!canCaptureProjectCover(detail, finalRender, finalFrameReady, runtimeSlideCount)) return;
    if (!finalRender || !manifestId) return;
    const captureKey = `${finalRender.id}:${manifestId}`;
    const attempts = projectCoverAttemptsRef.current.get(captureKey) || 0;
    if (attempts >= 3) return;
    projectCoverAttemptsRef.current.set(captureKey, attempts + 1);
    let cancelled = false;
    let retryTimer: number | undefined;
    const delay = attempts === 0 ? 250 : attempts * 1_500;
    const capture = async () => {
      try {
        const frameWindow = finalFrame.current?.contentWindow as (Window & {
          __captureProjectCover?: () => Promise<ProjectCoverCaptureResult>;
        }) | null;
        if (!frameWindow?.__captureProjectCover) throw new Error("预览运行时尚未提供项目封面截图能力");
        const result = await frameWindow.__captureProjectCover();
        if (cancelled) return;
        if (result.slideIndex !== 1
          || result.width !== 640
          || result.height !== 360
          || !["image/webp", "image/jpeg", "image/png"].includes(result.mimeType)
          || result.blob.type !== result.mimeType) {
          throw new Error("第一页封面截图结果不符合 640×360 图片约束");
        }
        const form = new FormData();
        form.append("request", JSON.stringify({ slideIndex: 1, captureRevision: manifestId }));
        form.append("cover", result.blob, `01.${result.extension}`);
        await userApi(`/api/app/v2/renders/${encodeURIComponent(finalRender.id)}/project-cover`, {
          method: "POST",
          body: form,
        });
        if (!cancelled) await load(true, true);
      } catch (caught) {
        debugFrontendEvent("project_cover_capture_failed", {
          taskId,
          renderId: finalRender.id,
          outputManifestId: manifestId,
          attempt: attempts + 1,
          error: caught instanceof Error ? caught.message : String(caught),
        });
        if (!cancelled && attempts + 1 < 3) {
          retryTimer = window.setTimeout(() => setProjectCoverRetryToken((value) => value + 1), 1_500 * (attempts + 1));
        }
      }
    };
    const startTimer = window.setTimeout(() => void capture(), delay);
    return () => {
      cancelled = true;
      window.clearTimeout(startTimer);
      if (retryTimer) window.clearTimeout(retryTimer);
    };
  }, [detail?.task.status, finalFrameReady, finalRender, load, projectCoverRetryToken, runtimeSlideCount, taskId]);

  useEffect(() => {
    const addition = detail?.slideAddition;
    const status = slideAdditionSubmitting ? "submitting" : addition?.status || (slideAdditionOpen ? "submitting" : "idle");
    const message = slideAdditionSubmitting
      ? "正在提交新增页面"
      : addition?.progressText || addition?.errorMessage || (slideAdditionOpen ? "请填写新增页面内容" : "");
    finalFrame.current?.contentWindow?.postMessage({
      type: "df-html-ppt-slide-addition-state",
      status,
      message,
    }, "*");
  }, [detail?.slideAddition, finalRender?.id, slideAdditionOpen, slideAdditionSubmitting]);

  useEffect(() => {
    const addition = detail?.slideAddition;
    if (addition?.status === "failed" || addition?.status === "cancelled") {
      setError(addition.errorMessage || "新增幻灯片失败，原演示文稿已保留");
    }
    if (finalRender && addition?.status === "succeeded" && addition.result?.renderId === finalRender.id) {
      const target = finalRender.outputManifest?.slides.length || effectiveTotalSlides;
      setCurrentSlide(Math.max(1, target));
      setFollowLatestProgressSlide(false);
    }
  }, [detail?.slideAddition, effectiveTotalSlides, finalRender]);

  const goToSlide = (slide: number) => {
    const maxSlide = finalRender ? effectiveTotalSlides : availableSlides;
    const target = Math.max(1, Math.min(slide, Math.max(1, maxSlide)));
    setFollowLatestProgressSlide(false);
    setCurrentSlide(target);
    if (finalRender) {
      finalFrame.current?.contentWindow?.postMessage({ type: "df-html-ppt-go-to-slide", slide: target }, "*");
    } else {
      progressFrame.current?.goToSlide(target);
    }
  };
  const changeViewportZoom = (delta: number) => {
    const nextZoom = Math.max(50, Math.min(150, zoom + delta));
    if (nextZoom === zoom) return;
    setZoom(nextZoom);
    postFinalViewportZoom(nextZoom);
  };
  const resetViewportZoom = () => {
    setZoom(100);
    postFinalViewportZoom(100, true);
  };

  const selectSlideAdditionFiles = (files: FileList | null) => {
    if (!files) return;
    const next = [...files];
    if (slideAdditionFiles.length + next.length > 5) {
      setSlideAdditionError("最多上传 5 张图片");
      return;
    }
    const invalid = next.find((file) => !["image/png", "image/jpeg", "image/webp"].includes(file.type) || file.size > 10 * 1024 * 1024);
    if (invalid) {
      setSlideAdditionError(`图片 ${invalid.name} 格式不支持或超过 10 MB`);
      return;
    }
    setSlideAdditionError("");
    setSlideAdditionFiles((current) => [...current, ...next]);
  };

  const submitSlideAddition = async () => {
    const text = slideAdditionText.trim();
    if (!finalRender || slideAdditionSubmitting || slideAdditionActive) return;
    if (text.length < 2 || text.length > 6_000) {
      setSlideAdditionError("请输入 2–6000 个字符的页面内容");
      return;
    }
    setSlideAdditionSubmitting(true);
    setSlideAdditionError("");
    try {
      const saved = await flushDeckStateSave();
      if (!saved) throw new Error("当前页面编辑尚未保存，请稍后重试");
      const approved = await requestCreditSpend({ operation: "master_ai_generate", slideCount: 1 });
      if (!approved) return;
      const images = await Promise.all(slideAdditionFiles.map(async (file) => ({
        originalName: file.name,
        mimeType: file.type,
        dataUrl: await fileToDataUrl(file),
      })));
      await userApi(`/api/app/v2/ppt-tasks/${encodeURIComponent(taskId)}/slides`, {
        method: "POST",
        body: JSON.stringify({
          baseRenderId: finalRender.id,
          idempotencyKey: clientId(),
          text,
          images,
        }),
      });
      notifyCreditActionSubmitted();
      setSlideAdditionOpen(false);
      setSlideAdditionText("");
      setSlideAdditionFiles([]);
      await load(true);
    } catch (caught) {
      setSlideAdditionError(caught instanceof Error ? caught.message : "新增幻灯片提交失败");
      setRequestId(caught instanceof ApiError ? caught.requestId || "" : "");
    } finally {
      setSlideAdditionSubmitting(false);
    }
  };

  const cancel = async () => {
    if (busy || !standardActive) return;
    setBusy("cancel");
    try {
      await userApi(`/api/app/v2/ppt-tasks/${encodeURIComponent(taskId)}/cancel`, { method: "POST", body: "{}" });
      await load();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "取消任务失败");
      setRequestId(caught instanceof ApiError ? caught.requestId || "" : "");
    } finally {
      setBusy("");
    }
  };

  const continueGeneration = async () => {
    if (busy || !canRetryStandardGeneration(detail, finalRender)) return;
    const planningRetry = !detail?.task.confirmedDeckVersionPublicId;
    if (!planningRetry) try {
      const generation = [...(detail?.generations || [])].reverse().find((item) =>
        item.productCode !== "advanced-beautify-image2" && item.productCode !== "image2-slide-revision-preview");
      const approved = await requestCreditSpend({
        operation: "master_ai_generate",
        slideCount: detail?.deckVersion?.document.slides.length || detail?.task.requestedPageCount || view.totalSlides || 1,
        ...(generation && generation.creditCost > 0 ? { fixedPoints: generation.creditCost } : {}),
      });
      if (!approved) return;
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "积分信息加载失败，请稍后重试");
      return;
    }
    setBusy("retry");
    try {
      await userApi(`/api/app/v2/ppt-tasks/${encodeURIComponent(taskId)}/continue`, {
        method: "POST",
        body: JSON.stringify({ idempotencyKey: clientId() }),
      });
      if (!planningRetry) notifyCreditActionSubmitted();
      setFollowLatestProgressSlide(true);
      await load();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "继续生成失败");
      setRequestId(caught instanceof ApiError ? caught.requestId || "" : "");
    } finally {
      setBusy("");
    }
  };

  const exportPptx = async (mode: PptxExportMode = exportModes[0]) => {
    if (!finalRender || busy) return;
    if (!exportModes.includes(mode)) {
      setExportError("当前版本仅支持导出可编辑 PPT。");
      return;
    }
    setExportMode(mode);
    setExportProgress({ phase: "preparing", message: "正在准备导出…", percent: 1 });
    setExportError("");
    setBusy("export");
    setError("");
    try {
      await waitForExportDialogPaint();
      await flushDeckStateSave();
      await exportOrDownloadPptx(finalRender, finalFrame.current, mode, setExportProgress, {
        forceRegenerate: deckDirty,
        title: detail?.deckVersion?.document.title || detail?.planningPreview?.snapshot.title || detail?.task.title,
      });
      setDeckDirty(false);
      await load(true);
      setExportOpen(false);
    } catch (caught) {
      const message = caught instanceof Error ? caught.message : "PPTX 导出失败";
      setExportError(message);
      setError(message);
      setRequestId(caught instanceof ApiError ? caught.requestId || "" : "");
    } finally {
      setBusy("");
      setExportProgress(undefined);
    }
  };

  const deleteProject = async () => {
    if (busy || ["planning", "generating"].includes(detail?.task.status || "")) return;
    setBusy("delete");
    setError("");
    try {
      await userApi(`/api/app/v2/ppt-tasks/${encodeURIComponent(taskId)}`, { method: "DELETE" });
      sessionStorage.removeItem(`pptdeep:task:${taskId}`);
      sessionStorage.removeItem(sourceContentPreviewStorageKey(taskId));
      navigate("/app/projects", { replace: true });
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "删除项目失败");
      setRequestId(caught instanceof ApiError ? caught.requestId || "" : "");
      setDeleteOpen(false);
    } finally {
      setBusy("");
    }
  };

  if (!detail && error) {
    return <section className="standard-generate-load-state">
      <strong>暂时无法打开生成页面</strong>
      <p>{error}</p>
      {requestId && <code>Request ID: {requestId}</code>}
      <button type="button" onClick={() => void load()}>重新加载</button>
    </section>;
  }

  if (!detail) return <div className="standard-generate-loading"><i/><strong>正在读取生成任务...</strong></div>;

  return <div className={`standard-generate-page phase-${view.phase}`} aria-busy={standardActive || slideAdditionActive}>
    <header className="standard-generate-topbar">
      <div className="standard-generate-titlebar">
        <button type="button" onClick={() => navigate("/app")} aria-label="返回工作台"><IconHome size={19}/></button>
        <h1>{detail.task.title}</h1>
        <span>已保存 {formatShortTime(detail.task.updatedAt)}</span>
        <IconCloudCheck size={20}/>
      </div>
      <GenerationModeTabs taskId={taskId} active="standard" advancedEnabled={advancedEnabled} showAdvanced={showAdvancedBeautify}/>
      <div className="standard-generate-actions">
        {retryAvailable && <button type="button" className="standard-recovery-cta" disabled={busy === "retry"} onClick={() => setRetryOpen(true)}><IconWand size={18}/>{busy === "retry" ? "正在恢复" : "继续生成"}</button>}
        <button type="button" disabled={!finalRender && !visibleProgressPreview?.previewUrl} onClick={() => void stageRef.current?.requestFullscreen()}><IconPlayerPlay size={18}/>预览播放</button>
        <button type="button" className="primary" disabled={!finalRender || busy === "export"} onClick={() => {
          if (finalRender?.renderer === "dfai") {
            setExportError("");
            setExportMode(undefined);
            setExportProgress(undefined);
            setExportOpen(true);
          } else {
            void exportPptx("image");
          }
        }}><IconDownload size={18}/>{busy === "export" ? "导出中" : "导出PPT"}</button>
        <div className="standard-more-actions">
          <button type="button" aria-label="更多操作" aria-haspopup="menu" aria-expanded={menuOpen} onClick={() => setMenuOpen((value) => !value)}><IconDots size={20}/></button>
          {menuOpen && <div className="standard-more-menu" role="menu">
            <button type="button" role="menuitem" className="danger" disabled={["planning", "generating"].includes(detail.task.status)} onClick={() => { setMenuOpen(false); setDeleteOpen(true); }}><IconTrash size={17}/>删除项目</button>
          </div>}
        </div>
      </div>
    </header>

    <div className={`standard-generate-workspace ${hasDeckFrame ? "show-deck-frame" : ""} ${assistantCollapsed ? "assistant-collapsed" : ""}`}>
      <aside className="standard-generate-rail">
        <div className="standard-generate-thumbs">
          {railSlides.map((slide) => <button
            type="button"
            key={slide.index}
            className={[slide.index === activeSlide ? "active" : "", ["failed", "cancelled"].includes(slide.status) ? slide.status : ""].filter(Boolean).join(" ")}
            aria-current={slide.index === activeSlide ? "page" : undefined}
            aria-label={`第 ${slide.index} 页，${slide.title}，${railSlideStatusLabel(slide.status)}`}
            onClick={() => goToSlide(slide.index)}
          >
            <b>{slide.index}</b>
            <span className={`standard-thumb standard-thumb-${slide.status}${slide.thumbnailUrl ? " has-thumbnail" : " no-thumbnail"}`}>
              {slide.thumbnailUrl ? <img src={slide.thumbnailUrl} alt={`第 ${slide.index} 页缩略图`}/> : <ThumbSkeleton slide={slide}/>}
              {slide.status === "generating" && !slide.thumbnailUrl && <i><IconLoader2 size={22}/><em>生成中</em></i>}
              {slide.status === "failed" && !slide.thumbnailUrl && <i><IconAlertTriangle size={20}/><em>生成失败</em></i>}
              {slide.status === "cancelled" && !slide.thumbnailUrl && <i><IconClock size={18}/><em>已取消</em></i>}
              {slide.status === "waiting" && !slide.thumbnailUrl && <i><IconClock size={18}/><em>{["failed", "cancelled"].includes(view.phase) ? "未生成" : "待生成"}</em></i>}
              {slide.status === "complete" && !slide.thumbnailUrl && <span className="standard-thumb-complete-indicator" aria-hidden="true"><IconCheck size={13}/><em>已生成</em></span>}
            </span>
          </button>)}
        </div>
        <strong className="standard-page-count">{activeSlide} / {formatTotalSlides(effectiveTotalSlides)} 页</strong>
        {finalRender?.renderer === "dfai" && <button
          type="button"
          className={`standard-create-slide${slideAdditionActive ? " is-loading" : ""}`}
          disabled={slideAdditionActive || (finalRender.outputManifest?.slides.length || 0) >= 100}
          onClick={openSlideAddition}
          title={
            (finalRender.outputManifest?.slides.length || 0) >= 100
              ? "标准模式生成页已达到 100 页上限"
              : "使用标准模式生成一张新幻灯片"
          }
        >{slideAdditionActive ? <><IconLoader2 size={17}/>生成中…</> : <><IconPlus size={17}/>新建幻灯片</>}</button>}
        <button type="button" className="standard-notes"><IconFolder size={18}/>演讲者备注</button>
      </aside>

      <main className="standard-generate-main">
        {showProgressOnly ? (
          <section className={`standard-progress-hero ${showWaitingContent ? "with-outline" : ""} ${showExpandedOutline ? "outline-only" : ""} phase-${view.phase}`}>
            <div className={`standard-progress-summary${showExpandedOutline ? " is-compact" : ""}`}>
              <div className="standard-magic-orb"><IconWand size={38}/></div>
              <div className="standard-progress-kicker"><span aria-hidden="true"/>标准模式 · {progressStatusKicker(view)}</div>
              <h2>{view.headline}</h2>
              <h3>{view.subline}</h3>
              <div className="standard-progress-line" role="progressbar" aria-label={`当前生成进度 ${view.percent}%`} aria-valuemin={0} aria-valuemax={100} aria-valuenow={view.percent}><span style={{ width: `${view.percent}%` }}/></div>
              <div className="standard-progress-copy"><b>{view.percent}%</b><span className={["starting", "outline", "rendering"].includes(view.phase) ? "loading" : ""}>{view.phase === "failed" ? <IconAlertTriangle size={18}/> : view.phase === "complete" ? <IconCheck size={18}/> : <IconLoader2 size={18}/>} {view.detailText}</span></div>
            </div>
            {showOutlineInHero && detail.planningPreview
              ? <OutlineOverview
                key={outlinePreviewKey}
                title={detail.planningPreview.snapshot.title || detail.task.title}
                slides={outlineSlides}
                animationKey={outlinePreviewKey}
                active={modelStreamActive}
              />
              : showSourceContentInHero && sourceContentPreview
                ? <SourceContentOverview
                  key={sourcePreviewRefKey}
                  preview={sourceContentPreview}
                  animationKey={sourcePreviewRefKey}
                  active={modelStreamActive}
                />
                : null}
          </section>
        ) : (
          <section className="standard-ppt-stage" ref={stageRef}>
            {finalRender?.previewUrl
              ? <>
                {!finalFrameReady && <div className="standard-ppt-stage-layer">
                  <PreviewSkeleton/>
                </div>}
                <iframe
                  ref={finalFrame}
                  className={finalFrameReady ? "is-ready" : "is-loading"}
                  src={finalRender.previewUrl}
                  title="标准模式正式预览"
                  onLoad={() => {
                    goToSlide(currentSlide);
                    postFinalViewportZoom(zoom, true);
                    postFinalAiGeneratedLabel(isChinaEdition);
                  }}
                />
              </>
              : visibleProgressPreview?.previewUrl
                ? <ProgressDeckFrame ref={progressFrame} taskId={taskId} preview={visibleProgressPreview} selectedSlide={activeSlide} showAiGeneratedLabel={isChinaEdition} onSlideChange={handleProgressSlideChange} onFirstSlideReady={setProgressCoverReadyRevision}/>
                : null}
          </section>
        )}
        {isChinaEdition && !(finalRender?.previewUrl && finalRender.renderer === "dfai") && !visibleProgressPreview?.previewUrl && <span className="ai-generated-label" aria-label="包含AI生成标识">包含AI生成</span>}

      </main>

      <aside className={`standard-assistant${assistantCollapsed ? " is-collapsed" : ""}`}>
        <div className="standard-assistant-head">
          {!assistantCollapsed && <div><strong>生成进度</strong><span>标准模式 · 自动刷新</span></div>}
          <button
            type="button"
            aria-label={assistantCollapsed ? "展开生成进度" : "折叠生成进度"}
            title={assistantCollapsed ? "展开生成进度" : "折叠生成进度"}
            aria-expanded={!assistantCollapsed}
            onClick={() => setAssistantCollapsed((collapsed) => !collapsed)}
          >
            {assistantCollapsed ? <IconChevronLeft size={20}/> : <IconChevronRight size={20}/>}
          </button>
        </div>
        {!assistantCollapsed && <div className="standard-assistant-scroll">
          <GenerationProgressPanel detail={detail} view={view}/>
          {detail.slideAddition && ["queued", "running"].includes(detail.slideAddition.status) && <div className="standard-slide-addition-progress" role="status">
            <span><IconLoader2 size={18}/></span>
            <div><strong>正在新增幻灯片</strong><small>{detail.slideAddition.progressText || "正在准备单页生成"}</small></div>
            <b>{detail.slideAddition.progressPercent}%</b>
            <em><i style={{ width: `${detail.slideAddition.progressPercent}%` }}/></em>
          </div>}
          <StageTimeline view={view}/>
          <SlideProgressList slides={railSlides} view={railView}/>
          {error && <div className="standard-assistant-error"><strong>{friendlyError(error)}</strong>{requestId && <span>Request ID: {requestId}</span>}</div>}
          {refreshNotice && <div className="standard-assistant-refresh-notice" role="status"><IconClock size={16}/><span>{refreshNotice}</span>{refreshRequestId && <small>Request ID: {refreshRequestId}</small>}</div>}
          <p className="standard-assistant-tip">{progressHelpText(view)}</p>
        </div>}
        {!assistantCollapsed && <div className="standard-control-row">
          {standardActive
            ? <button type="button" disabled={busy === "cancel"} onClick={() => void cancel()}>{busy === "cancel" ? "取消中..." : "取消任务"}</button>
            : retryAvailable
              ? <button type="button" disabled={busy === "retry"} onClick={() => setRetryOpen(true)}>{busy === "retry" ? "提交中..." : "继续生成"}</button>
              : <button type="button" onClick={() => void load()}>刷新状态</button>}
        </div>}
      </aside>
    </div>

    {shouldShowStandardViewportControls(Boolean(finalRender?.previewUrl && finalRender.renderer === "dfai"), slideAdditionActive) && <div className="standard-zoom-bar">
      <button type="button" aria-label="适配页面（100%）" title="适配页面（100%）" disabled={zoom === 100} onClick={resetViewportZoom}><IconMaximize size={22}/></button>
      <div><button type="button" aria-label="缩小预览" title="缩小预览" disabled={zoom <= 50} onClick={() => changeViewportZoom(-10)}><IconMinus size={20}/></button><strong aria-live="polite">{zoom}%</strong><button type="button" aria-label="放大预览" title="放大预览" disabled={zoom >= 150} onClick={() => changeViewportZoom(10)}><IconPlus size={20}/></button></div>
    </div>}
    {slideAdditionOpen && finalRender && <div className="standard-delete-overlay standard-slide-addition-overlay" role="dialog" aria-modal="true" aria-labelledby="standard-slide-addition-title" aria-describedby="standard-slide-addition-description">
      <form ref={slideAdditionDialog} className="standard-slide-addition-dialog" onSubmit={(event) => { event.preventDefault(); void submitSlideAddition(); }}>
        <button type="button" className="standard-slide-addition-close" aria-label="关闭新增幻灯片窗口" disabled={slideAdditionSubmitting} onClick={closeSlideAddition}><IconX size={18}/></button>
        <span className="standard-slide-addition-kicker">标准模式 · 单页生成</span>
        <h2 id="standard-slide-addition-title">新建幻灯片</h2>
        <p id="standard-slide-addition-description">输入这一页要表达的内容，可附加最多 5 张参考图片。新页面会沿用当前主题并添加在末尾。</p>
        <label className="standard-slide-addition-field">
          <span>页面内容 <b>{slideAdditionText.length} / 6000</b></span>
          <textarea autoFocus required minLength={2} maxLength={6000} rows={8} value={slideAdditionText} disabled={slideAdditionSubmitting} placeholder="例如：总结本季度增长的三个核心驱动因素，并突出同比提升 28% 的结论……" onChange={(event) => setSlideAdditionText(event.target.value)}/>
        </label>
        <div className="standard-slide-addition-images">
          <div><strong>参考图片</strong><span>PNG、JPG 或 WebP，单张不超过 10 MB</span></div>
          <label className={slideAdditionFiles.length >= 5 ? "disabled" : ""}>
            <IconPhoto size={18}/>选择图片
            <input type="file" accept="image/png,image/jpeg,image/webp" multiple disabled={slideAdditionSubmitting || slideAdditionFiles.length >= 5} onChange={(event) => { selectSlideAdditionFiles(event.target.files); event.currentTarget.value = ""; }}/>
          </label>
        </div>
        {slideAdditionFiles.length > 0 && <ul className="standard-slide-addition-file-list">
          {slideAdditionFiles.map((file, index) => <li key={`${file.name}:${file.size}:${index}`}><IconPhoto size={16}/><span>{file.name}</span><small>{formatFileSize(file.size)}</small><button type="button" aria-label={`移除图片 ${file.name}`} disabled={slideAdditionSubmitting} onClick={() => setSlideAdditionFiles((files) => files.filter((_, itemIndex) => itemIndex !== index))}><IconX size={15}/></button></li>)}
        </ul>}
        {slideAdditionError && <div className="standard-slide-addition-error" role="alert"><IconAlertTriangle size={17}/><span>{slideAdditionError}</span></div>}
        <div className="standard-slide-addition-actions"><button type="button" disabled={slideAdditionSubmitting} onClick={closeSlideAddition}>取消</button><button type="submit" className="primary" disabled={slideAdditionSubmitting || slideAdditionText.trim().length < 2}>{slideAdditionSubmitting ? <><IconLoader2 size={17}/>提交中…</> : "生成这一页"}</button></div>
      </form>
    </div>}
    {retryOpen && retryAvailable && <div className="standard-delete-overlay" role="dialog" aria-modal="true" aria-labelledby="standard-retry-title" aria-describedby="standard-retry-description">
      <section className="standard-delete-dialog standard-retry-dialog">
        <span><IconWand size={24}/></span>
        <h2 id="standard-retry-title">确认继续生成？</h2>
        <p id="standard-retry-description">将从上一次成功步骤继续生成剩余部分，未完成的环节会按标准流程重跑。是否继续？</p>
        <div><button type="button" autoFocus onClick={() => setRetryOpen(false)}>取消</button><button type="button" className="primary" onClick={() => { setRetryOpen(false); void continueGeneration(); }}>继续生成</button></div>
      </section>
    </div>}
    {deleteOpen && <div className="standard-delete-overlay" role="dialog" aria-modal="true" aria-labelledby="standard-delete-title" aria-describedby="standard-delete-description">
      <section className="standard-delete-dialog">
        <span><IconTrash size={24}/></span>
        <h2 id="standard-delete-title">删除这个项目？</h2>
        <p id="standard-delete-description">项目会从项目列表移除，相关文件进入待清理区并保留 30 天，之后自动彻底清理。删除后页面内无法自行恢复。</p>
        <div><button type="button" autoFocus disabled={busy === "delete"} onClick={() => setDeleteOpen(false)}>取消</button><button type="button" className="danger" disabled={busy === "delete"} onClick={() => void deleteProject()}>{busy === "delete" ? "删除中..." : "确认删除"}</button></div>
      </section>
    </div>}
    {exportOpen && <PptxExportChoiceDialog
      allowedModes={exportModes}
      busy={busy === "export"}
      error={exportError}
      progress={exportProgress}
      selectedMode={exportMode}
      onClose={() => {
        setExportError("");
        setExportOpen(false);
      }}
      onChoose={(mode) => void exportPptx(mode)}
    />}
  </div>;
}

const ProgressDeckFrame = forwardRef<DeckFrameHandle, {
  taskId: string;
  preview: ProjectProgressDeckPreview;
  selectedSlide: number;
  showAiGeneratedLabel: boolean;
  onSlideChange: (slide: number) => void;
  onFirstSlideReady: (captureRevision: string) => void;
}>(function ProgressDeckFrame({ taskId, preview, selectedSlide, showAiGeneratedLabel, onSlideChange, onFirstSlideReady }, ref) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const fallbackTimer = useRef<number | undefined>(undefined);
  const retryTimer = useRef<number | undefined>(undefined);
  const pendingUpdatedAt = useRef("");
  const resolvedUpdatedAt = useRef("");
  const refreshStartedAt = useRef(0);
  const refreshTimeoutMs = useRef(MIN_PROGRESS_DECK_REFRESH_TIMEOUT_MS);
  const retryVersion = useRef("");
  const retryAttempts = useRef(0);
  const renderedCaptureRevision = useRef(progressProjectCoverRevision(preview));
  const requestedSlide = useRef(Math.max(1, Number(selectedSlide) || 1));
  const [retryNonce, setRetryNonce] = useState(0);
  const previewIdentity = progressDeckPreviewIdentity(preview);
  const iframeIdentity = useRef(previewIdentity);
  const [iframeSrc, setIframeSrc] = useState(progressDeckFrameUrl(preview));
  const [loaded, setLoaded] = useState(false);
  const [refreshError, setRefreshError] = useState("");
  const postGoToSlide = useCallback((slide: number) => {
    requestedSlide.current = slide;
    iframeRef.current?.contentWindow?.postMessage({ type: "df-html-ppt-go-to-slide", slide }, "*");
  }, []);
  const postAiGeneratedLabel = useCallback(() => {
    const frameWindow = iframeRef.current?.contentWindow as (Window & {
      __setDFAIAiGeneratedLabel?: (visible: boolean) => void;
    }) | null;
    if (!frameWindow) return;
    frameWindow.postMessage({ type: "df-html-ppt-set-ai-generated-label", visible: showAiGeneratedLabel }, "*");
    try {
      frameWindow.__setDFAIAiGeneratedLabel?.(showAiGeneratedLabel);
    } catch {
      // Cross-origin preview URLs rely on the postMessage path above.
    }
  }, [showAiGeneratedLabel]);

  const captureProjectCover = useCallback(async () => {
    const frameWindow = iframeRef.current?.contentWindow as (Window & {
      __captureProjectCover?: () => Promise<ProjectCoverCaptureResult>;
    }) | null;
    if (!frameWindow?.__captureProjectCover) throw new Error("增量预览运行时尚未提供项目封面截图能力");
    return frameWindow.__captureProjectCover();
  }, []);

  useImperativeHandle(ref, () => ({ goToSlide: postGoToSlide, captureProjectCover }), [captureProjectCover, postGoToSlide]);

  useEffect(() => {
    if (iframeIdentity.current !== previewIdentity) {
      iframeIdentity.current = previewIdentity;
      setIframeSrc(progressDeckFrameUrl(preview));
      setLoaded(false);
      pendingUpdatedAt.current = "";
      resolvedUpdatedAt.current = "";
      refreshStartedAt.current = 0;
      refreshTimeoutMs.current = MIN_PROGRESS_DECK_REFRESH_TIMEOUT_MS;
      retryVersion.current = "";
      retryAttempts.current = 0;
      setRefreshError("");
      requestedSlide.current = Math.max(1, Number(selectedSlide) || 1);
      renderedCaptureRevision.current = progressProjectCoverRevision(preview);
      if (fallbackTimer.current) window.clearTimeout(fallbackTimer.current);
      if (retryTimer.current) window.clearTimeout(retryTimer.current);
      return;
    }
    if (preview.previewUrl) setIframeSrc((previous) => previous || progressDeckFrameUrl(preview));
  }, [preview.previewUrl, previewIdentity, selectedSlide]);

  const retryRefresh = useCallback((version: string) => {
    if (fallbackTimer.current) window.clearTimeout(fallbackTimer.current);
    pendingUpdatedAt.current = "";
    refreshStartedAt.current = 0;
    if (retryVersion.current !== version) {
      retryVersion.current = version;
      retryAttempts.current = 0;
    }
    if (retryAttempts.current >= 3) {
      pendingUpdatedAt.current = "";
      resolvedUpdatedAt.current = version;
      setRefreshError("新增页面预览暂未更新，当前页面已保留，可重试增量更新。");
      debugFrontendEvent("progress_deck_incremental_refresh_exhausted", {
        taskId,
        iframeIdentity: iframeIdentity.current,
        attemptNo: preview.attemptNo,
        revision: preview.revision,
        retryAttempt: retryAttempts.current,
      });
      return;
    }
    const delay = 1000 * (2 ** retryAttempts.current);
    retryAttempts.current += 1;
    if (retryTimer.current) window.clearTimeout(retryTimer.current);
    retryTimer.current = window.setTimeout(() => setRetryNonce((value) => value + 1), delay);
  }, [preview.attemptNo, preview.revision, taskId]);

  const retryIncrementalRefresh = useCallback(() => {
    pendingUpdatedAt.current = "";
    resolvedUpdatedAt.current = "";
    refreshStartedAt.current = 0;
    retryVersion.current = "";
    retryAttempts.current = 0;
    setRefreshError("");
    setRetryNonce((value) => value + 1);
  }, []);

  const requestRefresh = useCallback(() => {
    const updatedAt = String(preview.revision ?? preview.updatedAt ?? "");
    if (!loaded || !preview.incremental || !preview.stateUrl || !updatedAt) return;
    if (pendingUpdatedAt.current === updatedAt || resolvedUpdatedAt.current === updatedAt) return;
    pendingUpdatedAt.current = updatedAt;
    refreshStartedAt.current = Date.now();
    setRefreshError("");
    debugFrontendEvent("progress_deck_refresh_requested", {
      taskId,
      iframeIdentity: iframeIdentity.current,
      attemptNo: preview.attemptNo,
      revision: preview.revision,
      selectedSlide,
      retryAttempt: retryAttempts.current,
    });
    iframeRef.current?.contentWindow?.postMessage({
      type: "df-progress-deck-refresh",
      stateUrl: preview.stateUrl,
      updatedAt,
      attemptNo: preview.attemptNo,
      revision: preview.revision,
      targetSlide: selectedSlide,
    }, "*");
    if (fallbackTimer.current) window.clearTimeout(fallbackTimer.current);
    fallbackTimer.current = window.setTimeout(() => {
      if (pendingUpdatedAt.current === updatedAt) retryRefresh(updatedAt);
    }, refreshTimeoutMs.current);
  }, [loaded, preview.attemptNo, preview.incremental, preview.revision, preview.stateUrl, preview.updatedAt, retryNonce, retryRefresh, selectedSlide, taskId]);

  useEffect(() => { requestRefresh(); }, [requestRefresh]);
  useEffect(() => {
    const receive = (event: MessageEvent) => {
      if (event.source !== iframeRef.current?.contentWindow) return;
      const data = event.data as {
        type?: string;
        updatedAt?: unknown;
        revision?: unknown;
        currentSlide?: unknown;
        slide?: unknown;
        slideCount?: unknown;
        completedSlideCount?: unknown;
        placeholderCount?: unknown;
      };
      if (data?.type === "df-html-ppt-ready") {
        postAiGeneratedLabel();
        const readyRevision = Math.max(0, Number(data.revision) || 0);
        debugFrontendEvent("progress_deck_iframe_ready", {
          taskId,
          iframeIdentity: iframeIdentity.current,
          attemptNo: preview.attemptNo,
          revision: readyRevision,
          slideCount: Math.max(0, Number(data.slideCount) || 0),
          completedSlideCount: Math.max(0, Number(data.completedSlideCount) || 0),
          placeholderCount: Math.max(0, Number(data.placeholderCount) || 0),
        });
        if (readyRevision === Number(preview.revision || 0)) {
          renderedCaptureRevision.current = progressProjectCoverRevision(preview);
          if (canCaptureProgressProjectCover(preview, renderedCaptureRevision.current)) {
            onFirstSlideReady(renderedCaptureRevision.current);
          }
        }
      }
      if (data?.type === "df-progress-deck-refresh-applied") {
        const updatedAt = String(data.revision || data.updatedAt || pendingUpdatedAt.current || "");
        if (pendingUpdatedAt.current && updatedAt && updatedAt !== pendingUpdatedAt.current) return;
        if (fallbackTimer.current) window.clearTimeout(fallbackTimer.current);
        if (retryTimer.current) window.clearTimeout(retryTimer.current);
        const elapsedMs = refreshStartedAt.current ? Date.now() - refreshStartedAt.current : 0;
        debugFrontendEvent("progress_deck_refresh_applied", {
          taskId,
          iframeIdentity: iframeIdentity.current,
          attemptNo: preview.attemptNo,
          revision: data.revision || preview.revision,
          selectedSlide,
          elapsedMs,
          slideCount: Math.max(0, Number(data.slideCount) || 0),
          completedSlideCount: Math.max(0, Number(data.completedSlideCount) || 0),
          placeholderCount: Math.max(0, Number(data.placeholderCount) || 0),
        });
        refreshTimeoutMs.current = nextProgressDeckRefreshTimeoutMs(refreshTimeoutMs.current, elapsedMs);
        refreshStartedAt.current = 0;
        pendingUpdatedAt.current = "";
        resolvedUpdatedAt.current = updatedAt;
        retryVersion.current = "";
        retryAttempts.current = 0;
        setRefreshError("");
        const appliedRevision = Math.max(0, Number(data.revision) || 0);
        if (appliedRevision === Number(preview.revision || 0)) {
          renderedCaptureRevision.current = progressProjectCoverRevision(preview);
          if (canCaptureProgressProjectCover(preview, renderedCaptureRevision.current)) {
            onFirstSlideReady(renderedCaptureRevision.current);
          }
        }
      }
      if (data?.type === "df-progress-deck-refresh-failed") {
        const failedVersion = String(data.revision || data.updatedAt || pendingUpdatedAt.current || preview.revision || preview.updatedAt || "");
        if (pendingUpdatedAt.current && failedVersion && failedVersion !== pendingUpdatedAt.current) return;
        debugFrontendEvent("progress_deck_refresh_failed", {
          taskId,
          iframeIdentity: iframeIdentity.current,
          attemptNo: preview.attemptNo,
          revision: data.revision || preview.revision,
          selectedSlide,
          elapsedMs: refreshStartedAt.current ? Date.now() - refreshStartedAt.current : 0,
          retryAttempt: retryAttempts.current,
        });
        refreshStartedAt.current = 0;
        retryRefresh(failedVersion);
      }
      if (data?.type === "df-html-ppt-slide-change") {
        if (pendingUpdatedAt.current) return;
        const change = resolveProgressDeckSlideChange(requestedSlide.current, data.slide);
        requestedSlide.current = change.pendingRequestedSlide;
        if (change.userInitiated) onSlideChange(change.slide);
      }
    };
    window.addEventListener("message", receive);
    return () => window.removeEventListener("message", receive);
  }, [onFirstSlideReady, onSlideChange, postAiGeneratedLabel, preview, retryRefresh, selectedSlide, taskId]);
  useEffect(() => {
    if (loaded) postGoToSlide(selectedSlide);
  }, [loaded, postGoToSlide, selectedSlide]);
  useEffect(() => () => {
    if (fallbackTimer.current) window.clearTimeout(fallbackTimer.current);
    if (retryTimer.current) window.clearTimeout(retryTimer.current);
  }, []);

  return iframeSrc
    ? <div className="standard-progress-frame-shell">
      <iframe ref={iframeRef} src={iframeSrc} title="生成中真实预览" sandbox="allow-scripts allow-same-origin" onLoad={() => {
        setLoaded(true);
        iframeRef.current?.contentWindow?.postMessage({ type: "df-html-ppt-request-ready" }, "*");
        postAiGeneratedLabel();
        debugFrontendEvent("progress_deck_iframe_loaded", {
          taskId,
          iframeIdentity: iframeIdentity.current,
          attemptNo: preview.attemptNo,
          revision: preview.revision,
        });
      }}/>
      {refreshError && <div className="standard-progress-refresh-error" role="alert">
        <span>{refreshError}</span>
        <button type="button" onClick={retryIncrementalRefresh}>重试增量更新</button>
      </div>}
    </div>
    : <PreviewSkeleton/>;
});

function GenerationProgressPanel({ detail, view }: { detail: ProjectDetailResponse; view: GenerationViewState }) {
  const updatedAt = detail.generationProgressDeckPreview?.updatedAt
    || detail.generationProgressDeckPreview?.stateUpdatedAt
    || detail.task.updatedAt;
  return <section className={`standard-assistant-progress phase-${view.phase}`} aria-live="polite">
    <div className="standard-assistant-progress-top">
      {view.phase === "complete" ? <IconCheck size={25}/> : view.phase === "failed" ? <IconAlertTriangle size={25}/> : view.phase === "cancelled" ? <IconClock size={25}/> : <IconLoader2 size={25}/>}
      <div>
        <span className="standard-assistant-progress-kicker">{progressStatusKicker(view)}状态</span>
        <strong>{progressHeadline(view)}</strong>
        <small>{view.detailText}</small>
      </div>
    </div>
    <div className="standard-assistant-progress-bar" role="progressbar" aria-label={`当前生成进度 ${view.percent}%`} aria-valuemin={0} aria-valuemax={100} aria-valuenow={view.percent}>
      <span style={{ width: `${view.percent}%` }}/>
    </div>
    <dl className="standard-progress-stats">
      <div><dt>总进度</dt><dd>{view.percent}%</dd></div>
      <div><dt>页面</dt><dd>{view.completedSlides}/{formatTotalSlides(view.totalSlides)}</dd></div>
      <div><dt>更新</dt><dd>{formatShortTime(updatedAt)}</dd></div>
    </dl>
  </section>;
}

function StageTimeline({ view }: { view: GenerationViewState }) {
  const terminalInterrupted = view.phase === "failed" || view.phase === "cancelled";
  const outlineDone = ["outline", "rendering", "complete"].includes(view.phase) || (terminalInterrupted && view.percent >= 45);
  const renderingStarted = ["rendering", "complete"].includes(view.phase) || (terminalInterrupted && view.completedSlides > 0);
  return <section className="standard-stage-card">
    <TimelineItem done={outlineDone} active={view.phase === "starting"} title="资料与图片理解" text="读取输入资料、OCR文字并理解业务图片"/>
    <TimelineItem done={outlineDone} muted={view.phase === "starting"} title="叙事大纲" text={outlineDone ? "页面结构、核心表达与素材归属已确定" : "正在组织汇报逻辑与逐页内容"}/>
    <TimelineItem done={renderingStarted} active={view.phase === "outline"} muted={terminalInterrupted} title="页面内容与版式" text={view.phase === "outline" ? view.detailText : "填写页面内容并匹配标准主题"}/>
    <TimelineItem done={view.phase === "complete"} active={view.phase === "rendering"} muted={terminalInterrupted} title={`页面预览 ${view.completedSlides}/${formatTotalSlides(view.totalSlides)}`} text={view.phase === "rendering" ? `正在处理第 ${view.currentSlide} 页` : view.phase === "complete" ? "正式预览已就绪，可导出图片型 PPTX" : "等待页面内容与版式完成"}/>
  </section>;
}

function TimelineItem({ done, active, muted, title, text }: { done?: boolean; active?: boolean; muted?: boolean; title: string; text: string }) {
  return <div className={done ? "done" : active ? "active" : muted ? "muted" : ""}>
    <span>{done ? <IconCheck size={14}/> : active ? <IconLoader2 size={17}/> : <IconPlus size={14}/>}</span>
    <p><strong>{title}</strong><small>{text}</small></p>
  </div>;
}

function SlideProgressList({ slides, view }: { slides: RailSlide[]; view: GenerationViewState }) {
  const visibleSlides = slides.slice(0, view.totalSlides);
  return <section className="standard-slide-progress-card" aria-label="页面生成进度">
    <div className="standard-slide-progress-head"><strong>页面生成</strong><span>{view.completedSlides}/{formatTotalSlides(view.totalSlides)} 页</span></div>
    <div className="standard-slide-progress-list">
      {visibleSlides.map((slide) => {
        const loading = slide.status === "generating" && !["complete", "failed", "cancelled"].includes(view.phase);
        const done = slide.status === "complete" || view.phase === "complete";
        return <div className={`standard-slide-progress-row ${slide.status}`} key={slide.index}>
          <span className={loading ? "loading" : ""}>{done ? <IconCheck size={14}/> : slide.status === "failed" ? <IconAlertTriangle size={14}/> : loading ? <IconLoader2 size={15}/> : <IconClock size={14}/>}</span>
          <p><strong>{String(slide.index).padStart(2, "0")} {slide.title}</strong><small>{slideProgressText(slide, view)}</small></p>
        </div>;
      })}
    </div>
  </section>;
}

function OutlineOverview({ title, slides, animationKey, active }: {
  title: string;
  slides: NonNullable<ProjectDetailResponse["planningPreview"]>["snapshot"]["slides"];
  animationKey: string;
  active: boolean;
}) {
  const displaySlides = useMemo(() => slides || [], [slides]);
  const model = useMemo(() => buildOutlineContentStreamModel(title, displaySlides), [title, displaySlides]);
  const visibleCount = useModelStream(model, animationKey, active);
  const containerRef = useRef<HTMLDivElement>(null);
  const text = (key: string) => sourceContentStreamText(model, key, visibleCount);
  const started = (key: string) => sourceContentStreamFieldStarted(model, key, visibleCount);
  const streamingClass = (key: string) => sourceContentStreamFieldActive(model, key, visibleCount) ? " is-streaming" : "";
  const streamedSlides = displaySlides
    .map((slide, position) => ({ slide, position }))
    .filter(({ slide, position }) => started(outlineSlideFirstStreamKey(slide, position)));

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (container) container.scrollTop = container.scrollHeight;
  }, [visibleCount]);

  return <div className="standard-outline-preview" ref={containerRef}>
    <div><span>内容大纲</span><h3 className={streamingClass("title")}>{text("title")}</h3></div>
    <div>
      {displaySlides.length ? streamedSlides.map(({ slide, position }) => {
        const titleKey = outlineSlideStreamKey(slide, position, "title");
        const detailKey = outlineSlideStreamKey(slide, position, "detail");
        return <article key={`${slide.index}:${slide.title}`}>
          <b>{String(slide.index).padStart(2, "0")}</b>
          <span>
            {started(titleKey) && <strong className={streamingClass(titleKey)}>{text(titleKey)}</strong>}
            {started(detailKey) && <small className={streamingClass(detailKey)}>{text(detailKey)}</small>}
          </span>
        </article>;
      }) : <PreviewSkeleton/>}
    </div>
  </div>;
}

interface SourceContentStreamSegment {
  key: string;
  start: number;
  end: number;
  graphemes: string[];
}

export interface SourceContentStreamModel {
  graphemes: string[];
  segments: SourceContentStreamSegment[];
}

interface ModelStreamField {
  key: string;
  value: string | undefined;
}

export function buildOutlineContentStreamModel(
  title: string,
  slides: NonNullable<NonNullable<ProjectDetailResponse["planningPreview"]>["snapshot"]["slides"]>,
): SourceContentStreamModel {
  const fields: ModelStreamField[] = [{ key: "title", value: title || "PPT 内容规划" }];
  slides.forEach((slide, position) => {
    fields.push(
      { key: outlineSlideStreamKey(slide, position, "title"), value: slide.title },
      {
        key: outlineSlideStreamKey(slide, position, "detail"),
        value: `${slide.roleLabel || slide.role} · ${slide.message || "已生成页面核心信息"}`,
      },
    );
  });
  return buildModelStream(fields);
}

export function buildSourceContentStreamModel(preview: ProjectSourceContentPreview): SourceContentStreamModel {
  const fields: ModelStreamField[] = [{ key: "title", value: preview.title || "已解析的制作要求与资料" }];
  preview.requirements.forEach((requirement) => fields.push({ key: `requirement:${requirement.id}`, value: requirement.text }));
  preview.materials.forEach((material) => {
    fields.push({ key: `material:${material.id}:heading`, value: material.heading || material.sourceName });
    if (material.heading && material.sourceName && material.heading !== material.sourceName) {
      fields.push({ key: `material:${material.id}:source`, value: material.sourceName });
    }
    fields.push({ key: `material:${material.id}:text`, value: material.text });
  });
  if (preview.truncated) {
    fields.push({ key: "footer", value: "资料内容较长，当前展示部分解析结果；完整资料仍会用于生成大纲。" });
  }
  return buildModelStream(fields);
}

function buildModelStream(fields: ModelStreamField[]): SourceContentStreamModel {
  const segments: SourceContentStreamSegment[] = [];
  const graphemes: string[] = [];
  const append = (key: string, value: string | undefined) => {
    const fieldGraphemes = splitSourceContentGraphemes(value || "");
    if (!fieldGraphemes.length) return;
    const start = graphemes.length;
    graphemes.push(...fieldGraphemes);
    segments.push({ key, start, end: graphemes.length, graphemes: fieldGraphemes });
  };
  fields.forEach((field) => append(field.key, field.value));
  return { graphemes, segments };
}

export function sourceContentStreamText(model: SourceContentStreamModel, key: string, visibleCount: number): string {
  const segment = model.segments.find((candidate) => candidate.key === key);
  if (!segment || visibleCount <= segment.start) return "";
  return segment.graphemes.slice(0, Math.min(segment.graphemes.length, visibleCount - segment.start)).join("");
}

export function sourceContentStreamFieldStarted(model: SourceContentStreamModel, key: string, visibleCount: number): boolean {
  const segment = model.segments.find((candidate) => candidate.key === key);
  return Boolean(segment && visibleCount >= segment.start);
}

export function sourceContentStreamFieldActive(model: SourceContentStreamModel, key: string, visibleCount: number): boolean {
  const segment = model.segments.find((candidate) => candidate.key === key);
  return Boolean(segment && visibleCount >= segment.start && visibleCount < segment.end);
}

function SourceContentOverview({ preview, animationKey, active }: {
  preview: ProjectSourceContentPreview;
  animationKey: string;
  active: boolean;
}) {
  const model = useMemo(() => buildSourceContentStreamModel(preview), [preview]);
  const visibleCount = useModelStream(model, animationKey, active);
  const containerRef = useRef<HTMLDivElement>(null);
  const text = (key: string) => sourceContentStreamText(model, key, visibleCount);
  const started = (key: string) => sourceContentStreamFieldStarted(model, key, visibleCount);
  const streamingClass = (key: string) => sourceContentStreamFieldActive(model, key, visibleCount) ? " is-streaming" : "";
  const visibleRequirements = preview.requirements.filter((requirement) => started(`requirement:${requirement.id}`));
  const visibleMaterials = preview.materials.filter((material) => started(sourceContentMaterialFirstStreamKey(material)));

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (container) container.scrollTop = container.scrollHeight;
  }, [visibleCount]);

  return <div className="standard-source-content-preview" ref={containerRef}>
    <header><span>资料解析结果</span><h3 className={streamingClass("title")}>{text("title")}</h3></header>
    {visibleRequirements.length > 0 && <section>
      <h4>制作要求</h4>
      {visibleRequirements.map((requirement) => {
        const key = `requirement:${requirement.id}`;
        return <article className="requirement" key={requirement.id}>
          <b>{String(preview.requirements.indexOf(requirement) + 1).padStart(2, "0")}</b><p className={streamingClass(key)}>{text(key)}</p>
        </article>;
      })}
    </section>}
    {visibleMaterials.length > 0 && <section>
      <h4>资料内容</h4>
      {visibleMaterials.map((material) => {
        const headingKey = `material:${material.id}:heading`;
        const sourceKey = `material:${material.id}:source`;
        const textKey = `material:${material.id}:text`;
        return <article className="material" key={material.id}>
          {(material.heading || material.sourceName) && started(headingKey) && <strong className={streamingClass(headingKey)}>{text(headingKey)}</strong>}
          {material.heading && material.sourceName && material.heading !== material.sourceName && started(sourceKey) && <small className={streamingClass(sourceKey)}>{text(sourceKey)}</small>}
          {started(textKey) && <p className={streamingClass(textKey)}>{text(textKey)}</p>}
        </article>;
      })}
    </section>}
    {preview.truncated && started("footer") && <footer className={streamingClass("footer")}>{text("footer")}</footer>}
  </div>;
}

function useModelStream(model: SourceContentStreamModel, animationKey: string, active: boolean): number {
  const [visibleCount, setVisibleCount] = useState(0);
  const modelRef = useRef(model);
  const runRef = useRef(0);
  modelRef.current = model;

  useLayoutEffect(() => {
    const run = runRef.current + 1;
    runRef.current = run;
    let timer: number | undefined;
    let cursor = 0;
    if (!active) {
      setVisibleCount(modelRef.current.graphemes.length);
      return () => { runRef.current += 1; };
    }
    setVisibleCount(0);

    const tick = () => {
      if (runRef.current !== run) return;
      const activeModel = modelRef.current;
      cursor = Math.min(activeModel.graphemes.length, cursor + 1);
      setVisibleCount(cursor);
      if (cursor < activeModel.graphemes.length) timer = window.setTimeout(tick, SOURCE_CONTENT_STREAM_INTERVAL_MS);
    };

    if (modelRef.current.graphemes.length) timer = window.setTimeout(tick, SOURCE_CONTENT_STREAM_INTERVAL_MS);
    return () => {
      runRef.current += 1;
      if (timer !== undefined) window.clearTimeout(timer);
    };
  }, [active, animationKey]);
  return visibleCount;
}

function splitSourceContentGraphemes(value: string): string[] {
  if (typeof Intl.Segmenter === "function") {
    return [...new Intl.Segmenter("zh-CN", { granularity: "grapheme" }).segment(value)].map((part) => part.segment);
  }
  return Array.from(value);
}

type OutlinePreviewSlide = NonNullable<NonNullable<ProjectDetailResponse["planningPreview"]>["snapshot"]["slides"]>[number];

function outlineSlideStreamKey(slide: OutlinePreviewSlide, position: number, field: "title" | "detail"): string {
  return `slide:${position}:${slide.index}:${field}`;
}

function outlineSlideFirstStreamKey(slide: OutlinePreviewSlide, position: number): string {
  return outlineSlideStreamKey(slide, position, slide.title ? "title" : "detail");
}

function sourceContentMaterialFirstStreamKey(material: ProjectSourceContentPreview["materials"][number]): string {
  return material.heading || material.sourceName ? `material:${material.id}:heading` : `material:${material.id}:text`;
}

function PreviewSkeleton() {
  return <div className="standard-page-skeleton">
    <div><IconWand size={44}/></div>
    <section><i/><i/><i/><span/><span/><span/><span/></section>
  </div>;
}

function ThumbSkeleton({ slide }: { slide: RailSlide }) {
  return <div className="standard-thumb-skeleton">
    <strong>{slide.title}</strong>
    {slide.subtitle ? <small>{slide.subtitle}</small> : <><span/><span/><span/></>}
  </div>;
}

export function deriveGenerationView(detail?: ProjectDetailResponse, finalRender?: ProjectRender): GenerationViewState {
  const progressPreview = isProgressPreviewForCurrentAttempt(detail, detail?.generationProgressDeckPreview)
    ? detail?.generationProgressDeckPreview
    : undefined;
  const totalSlides = Math.max(0,
    finalRender?.outputManifest?.slides.length || 0,
    progressPreview?.totalSlides || 0,
    detail?.deckVersion?.document.slides.length || 0,
    detail?.task.requestedPageCount || 0,
    detail?.planningPreview?.snapshot.pageCount || 0,
    detail?.planningPreview?.snapshot.slides?.length || 0,
  );
  const reportedCompletedSlides = finalRender ? totalSlides : progressPreview?.completedSlides ?? countCompletedSlideProgress(detail);
  const completedSlides = totalSlides > 0 ? Math.min(totalSlides, reportedCompletedSlides) : reportedCompletedSlides;
  const currentSlide = finalRender
    ? Math.max(1, totalSlides)
    : totalSlides > 0
      ? Math.min(totalSlides, Math.max(1, completedSlides + 1))
      : Math.max(1, completedSlides + 1);
  const status = detail?.task.status || "planning";
  const visibleFailure = projectHasVisibleFailure(detail, finalRender);
  const phase: GenerationPhase = finalRender ? "complete"
    : status === "failed" || visibleFailure ? "failed"
      : status === "cancelled" ? "cancelled"
        : progressPreview?.previewUrl || completedSlides > 0 || status === "generating" ? "rendering"
          : detail?.planningPreview ? "outline"
            : "starting";
  const percent = Math.min(100, Math.max(0, Math.round(
    finalRender ? 100 : progressPreview?.progress ?? detail?.task.progressPercent ?? (phase === "outline" ? 45 : 8),
  )));
  if (phase === "complete") return {
    phase, percent: 100, totalSlides, completedSlides: totalSlides, currentSlide,
    headline: "PPT 已生成完成",
    subline: `共 ${formatTotalSlides(totalSlides)} 页，可预览和导出`,
    detailText: "生成完成，正在等待您的检查",
  };
  if (phase === "failed") return {
    phase, percent, totalSlides, completedSlides, currentSlide,
    headline: "PPT 生成失败",
    subline: "可以稍后安全重试",
    detailText: projectFailureMessage(detail),
  };
  if (phase === "cancelled") return {
    phase, percent, totalSlides, completedSlides, currentSlide,
    headline: "生成已取消",
    subline: "大纲与已有结果仍然保留",
    detailText: "您可以在当前页面重新生成",
  };
  if (phase === "rendering") return {
    phase, percent, totalSlides, completedSlides, currentSlide,
    headline: "正在生成PPT",
    subline: `正在生成第 ${currentSlide} 页 / 共 ${formatTotalSlides(totalSlides)} 页`,
    detailText: progressPreview?.progressText || detail?.task.progressText || `已完成 ${completedSlides}/${formatTotalSlides(totalSlides)} 页，正在生成第 ${currentSlide} 页内容...`,
  };
  if (phase === "outline") return {
    phase, percent, totalSlides, completedSlides, currentSlide,
    headline: "正在生成PPT",
    subline: `大纲已完成 / 共 ${formatTotalSlides(totalSlides)} 页`,
    detailText: detail?.planningPreview?.snapshot.nextStep || detail?.planningPreview?.snapshot.progressText || "已收到大纲概览，正在匹配模板与填写页面内容...",
  };
  return {
    phase, percent, totalSlides, completedSlides, currentSlide,
    headline: "正在生成PPT",
    subline: "正在解析资料与规划内容",
    detailText: detail?.task.progressText || "正在理解资料、生成大纲并选择模板...",
  };
}

function countCompletedSlideProgress(detail?: ProjectDetailResponse) {
  return detail?.slideProgress.filter((slide) => slide.status === "succeeded" || slide.status === "complete").length || 0;
}

function formatTotalSlides(totalSlides: number): string {
  return totalSlides > 0 ? String(totalSlides) : "--";
}

export function visibleProgressSlideCount(
  detail: ProjectDetailResponse | undefined,
  fallbackPreview?: ProjectProgressDeckPreview,
): number {
  const current = isProgressPreviewForCurrentAttempt(detail, detail?.generationProgressDeckPreview)
    ? detail?.generationProgressDeckPreview
    : undefined;
  const fallback = isProgressPreviewForCurrentAttempt(detail, fallbackPreview)
    ? fallbackPreview
    : undefined;
  return Math.max(
    current?.completedSlides || 0,
    fallback?.completedSlides || 0,
    countCompletedSlideProgress(detail),
  );
}

export function buildRailSlides(detail: ProjectDetailResponse | undefined, finalRender: ProjectRender | undefined, completedSlides: number, generatingSlide: number, phase: GenerationPhase): RailSlide[] {
  if (finalRender?.outputManifest?.slides.length) {
    return finalRender.outputManifest.slides.map((slide) => ({
      index: slide.index,
      title: slide.title,
      status: "complete",
      thumbnailUrl: signedRenderResourceUrl(finalRender, slide.thumbnail?.url),
      kind: "generated",
    }));
  }
  const outlineSlides = detail?.planningPreview?.snapshot.slides || [];
  const deckSlides = detail?.deckVersion?.document.slides || [];
  const progressPreview = isProgressPreviewForCurrentAttempt(detail, detail?.generationProgressDeckPreview)
    ? detail?.generationProgressDeckPreview
    : undefined;
  const total = Math.max(0,
    progressPreview?.totalSlides || 0,
    deckSlides.length,
    detail?.task.requestedPageCount || 0,
    detail?.planningPreview?.snapshot.pageCount || 0,
    outlineSlides.length,
  );
  return Array.from({ length: total }, (_, item) => {
    const index = item + 1;
    const outline = outlineSlides.find((slide) => slide.index === index);
    const deck = deckSlides.find((slide) => slide.index === index);
    return {
      index,
      title: outline?.title || deck?.title || (index === 1 ? detail?.task.title || "封面" : `第 ${index} 页`),
      subtitle: outline?.message || deck?.coreMessage,
      status: index <= completedSlides ? "complete" : index === generatingSlide && phase === "failed" ? "failed" : index === generatingSlide && phase === "cancelled" ? "cancelled" : index === generatingSlide ? "generating" : "waiting",
    };
  });
}

function railSlideStatusLabel(status: RailSlideStatus): string {
  return {
    complete: "已生成",
    generating: "生成中",
    waiting: "待生成",
    failed: "生成失败",
    cancelled: "已取消",
  }[status];
}

export function extendRuntimeRailSlides(slides: RailSlide[], totalSlides: number): RailSlide[] {
  const normalizedTotal = Math.max(slides.length, Math.floor(Number(totalSlides) || 0));
  return Array.from({ length: normalizedTotal }, (_item, offset) => {
    const index = offset + 1;
    const existing = slides[offset];
    if (existing) return existing;
    return {
      index,
      title: "新增主题页面",
      status: "complete",
      kind: "theme-layout",
    };
  });
}

export function pickFinalRender(detail?: ProjectDetailResponse) {
  if (!detail) return undefined;
  if (hasActiveStandardGeneration(detail)) return undefined;
  const successful = detail.renders.filter((render) => render.status === "succeeded" && render.outputManifest && isStandardRender(render));
  const current = successful.find((render) => render.id === detail.task.currentRenderId && render.renderer === "dfai")
    || successful.find((render) => render.id === detail.task.currentRenderId);
  return current
    || [...successful].reverse().find((render) => render.renderer === "dfai")
    || [...successful].reverse().find((render) => render.renderer !== "image2")
    || [...successful].reverse()[0];
}

export function standardAssistantDefaultCollapsed(phase: GenerationPhase): boolean {
  return phase === "complete";
}

export function shouldRunStandardModelStream(taskStatus: string | undefined, phase: GenerationPhase): boolean {
  return !["failed", "cancelled", "succeeded", "archived"].includes(taskStatus || "")
    && !["failed", "cancelled", "complete"].includes(phase);
}

export function shouldShowStandardViewportControls(hasDFAIFinalPreview: boolean, slideAdditionActive: boolean): boolean {
  return hasDFAIFinalPreview && !slideAdditionActive;
}

export function canRetryStandardGeneration(detail?: ProjectDetailResponse, finalRender?: ProjectRender) {
  if (!detail || finalRender) return false;
  return ["failed", "cancelled"].includes(detail.task.status)
    || (detail.task.status === "ready_to_generate" && Number(detail.task.retryCount || 0) > 0)
    || projectHasVisibleFailure(detail, finalRender);
}

export function shouldPollStandardDetail(detail?: ProjectDetailResponse, finalRender?: ProjectRender) {
  if (!detail) return true;
  if (["queued", "running"].includes(detail.slideAddition?.status || "")) return true;
  if (hasActiveStandardGeneration(detail)) return true;
  if (!finalRender?.previewUrl && !["failed", "cancelled"].includes(detail.task.status)) {
    const progressPreview = isProgressPreviewForCurrentAttempt(detail, detail.generationProgressDeckPreview)
      ? detail.generationProgressDeckPreview
      : undefined;
    if (detail.task.status === "ready_to_generate") return true;
    if (progressPreview?.totalSlides && progressPreview.completedSlides < progressPreview.totalSlides) return true;
  }
  if (["planning", "generating"].includes(detail.task.status)) return !finalRender?.previewUrl;
  if (detail.task.status === "succeeded") return !finalRender?.previewUrl;
  return false;
}

export function shouldApplyTaskDetail(current: ProjectDetailResponse | undefined, incoming: ProjectDetailResponse) {
  if (!current) return true;
  const currentUpdatedAt = parseTime(current.task.updatedAt);
  const incomingUpdatedAt = parseTime(incoming.task.updatedAt);
  if (currentUpdatedAt && incomingUpdatedAt && incomingUpdatedAt < currentUpdatedAt) return false;
  if (currentUpdatedAt && incomingUpdatedAt === currentUpdatedAt) {
    const currentProgress = current.generationProgressDeckPreview;
    const incomingProgress = incoming.generationProgressDeckPreview;
    const sameAttempt = currentProgress && incomingProgress
      && Number(currentProgress.attemptNo || 0) === Number(incomingProgress.attemptNo || 0);
    if (sameAttempt && Number(incomingProgress.revision || 0) < Number(currentProgress.revision || 0)) return false;
  }
  return !(current.task.status === "succeeded"
    && incoming.task.status !== "succeeded"
    && incomingUpdatedAt <= currentUpdatedAt);
}

export function canCaptureProjectCover(
  detail: ProjectDetailResponse | undefined,
  render: ProjectRender | undefined,
  frameReady: boolean,
  runtimeSlideCount: number,
): boolean {
  const manifest = render?.outputManifest;
  const firstSlide = manifest?.slides.find((slide) => slide.index === 1);
  return Boolean(frameReady
    && detail?.task.status === "succeeded"
    && render?.renderer === "dfai"
    && render.status === "succeeded"
    && render.outputManifestId
    && manifest?.status === "succeeded"
    && firstSlide?.previewType === "html"
    && firstSlide.preview.mimeType === "text/html"
    && !firstSlide.thumbnail
    && runtimeSlideCount >= manifest.slides.length);
}

export function progressProjectCoverRevision(
  preview: Pick<ProjectProgressDeckPreview, "attemptNo" | "revision">,
): string {
  return `${Math.max(0, Number(preview.attemptNo || 0))}:${Math.max(1, Number(preview.revision || 0))}`;
}

export function canCaptureProgressProjectCover(
  preview: ProjectProgressDeckPreview | null | undefined,
  renderedRevision: string,
): preview is ProjectProgressDeckPreview {
  return Boolean(preview
    && preview.status === "succeeded"
    && preview.previewFidelity === "progress-deck-partial"
    && Number(preview.completedSlides || 0) >= 1
    && Number(preview.revision || 0) >= 1
    && renderedRevision === progressProjectCoverRevision(preview));
}

export function progressDeckPreviewIdentity(preview: Pick<ProjectProgressDeckPreview, "previewId" | "attemptNo">): string {
  return `${preview.previewId || "deck"}:${preview.attemptNo || 0}`;
}

export function resolveProgressDeckSlideChange(pendingRequestedSlide: number, reportedSlide: unknown) {
  const slide = Math.max(1, Number(reportedSlide) || 1);
  const requestedSlide = Math.max(0, Math.floor(Number(pendingRequestedSlide) || 0));
  if (requestedSlide > 0) {
    return {
      slide,
      pendingRequestedSlide: slide === requestedSlide ? 0 : requestedSlide,
      userInitiated: false,
    };
  }
  return { slide, pendingRequestedSlide: 0, userInitiated: true };
}

function progressDeckFrameUrl(preview: ProjectProgressDeckPreview): string {
  if (!preview.previewUrl) return "";
  const separator = preview.previewUrl.includes("?") ? "&" : "?";
  return `${preview.previewUrl}${separator}df_progress_revision=${encodeURIComponent(String(preview.revision || 0))}`;
}

export function sourceContentPreviewIdentity(preview: ProjectSourceContentPreviewRef): string {
  return `${preview.id}:${preview.attemptNo}:${preview.updatedAt}`;
}

export function planningPreviewIdentity(preview: NonNullable<ProjectDetailResponse["planningPreview"]>): string {
  return `${preview.id}:${preview.updatedAt}`;
}

export function standardCenterContentMode(input: {
  hasDeckFrame: boolean;
  hasPlanningPreview: boolean;
  hasSourceContentPreview: boolean;
}): "deck" | "outline" | "source" | "progress" {
  if (input.hasDeckFrame) return "deck";
  if (input.hasPlanningPreview) return "outline";
  if (input.hasSourceContentPreview) return "source";
  return "progress";
}

export interface TaskDetailRefreshState {
  inFlight: boolean;
  consecutivePollFailures: number;
}

export function beginTaskDetailRefresh(state: TaskDetailRefreshState) {
  if (state.inFlight) return false;
  state.inFlight = true;
  return true;
}

export function finishTaskDetailRefresh(state: TaskDetailRefreshState) {
  state.inFlight = false;
}

export function recordTaskDetailPollFailure(state: TaskDetailRefreshState) {
  state.consecutivePollFailures += 1;
  return state.consecutivePollFailures >= 3;
}

export function resetTaskDetailPollFailures(state: TaskDetailRefreshState) {
  state.consecutivePollFailures = 0;
}

export function nextProgressDeckRefreshTimeoutMs(previousTimeoutMs: number, elapsedMs: number) {
  const measuredTimeout = Math.ceil(Math.max(0, elapsedMs) * 2 + 2000);
  return Math.max(
    MIN_PROGRESS_DECK_REFRESH_TIMEOUT_MS,
    Math.min(MAX_PROGRESS_DECK_REFRESH_TIMEOUT_MS, Math.max(previousTimeoutMs, measuredTimeout)),
  );
}

function subscribeStandardGenerationEvents(
  taskId: string,
  onRefresh: () => void,
  onConnectionChange?: (connected: boolean) => void,
): () => void {
  const controller = new AbortController();
  let retryTimer: number | undefined;
  const eventUrl = `/api/app/v2/ppt-tasks/${encodeURIComponent(taskId)}/generation-events`;

  const start = async () => {
    debugFrontendEvent("standard_generation_sse_connecting", { taskId, eventUrl });
    try {
      const token = getUserAuthToken();
      const response = await fetch(eventUrl, {
        cache: "no-store",
        headers: token ? { authorization: `Bearer ${token}` } : {},
        signal: controller.signal,
      });
      if (!response.ok || !response.body) {
        debugFrontendEvent("standard_generation_sse_unavailable", { taskId, status: response.status, hasBody: Boolean(response.body) });
        return;
      }
      onConnectionChange?.(true);
      debugFrontendEvent("standard_generation_sse_connected", { taskId, status: response.status });
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      for (;;) {
        const chunk = await reader.read();
        if (chunk.done) break;
        buffer += decoder.decode(chunk.value, { stream: true });
        const events = buffer.split(/\r?\n\r?\n/);
        buffer = events.pop() || "";
        for (const event of events) {
          const eventType = /^event:\s*([^\r\n]+)\s*$/m.exec(event)?.[1]?.trim();
          if (eventType === "ready" || eventType === "slide_preview_ready") {
            const payload = parseStandardGenerationSsePayload(event);
            debugFrontendEvent("standard_generation_sse_event_received", {
              taskId,
              eventType,
              attemptNo: payload.attemptNo,
              revision: payload.revision,
              completedSlides: payload.completedSlides,
              totalSlides: payload.totalSlides,
            });
            onRefresh();
          }
        }
      }
      debugFrontendEvent("standard_generation_sse_stream_ended", { taskId });
    } catch (error) {
      if (controller.signal.aborted) return;
      debugFrontendEvent("standard_generation_sse_failed", {
        taskId,
        error: error instanceof Error ? error.message : String(error),
      });
    } finally {
      if (!controller.signal.aborted) onConnectionChange?.(false);
    }
    if (!controller.signal.aborted) {
      debugFrontendEvent("standard_generation_sse_retry_scheduled", { taskId, delayMs: 3000 });
      retryTimer = window.setTimeout(() => { void start(); }, 3000);
    }
  };

  void start();
  return () => {
    controller.abort();
    onConnectionChange?.(false);
    if (retryTimer) window.clearTimeout(retryTimer);
  };
}

function parseStandardGenerationSsePayload(event: string): Record<string, unknown> {
  const data = /^data:\s*(.+)\s*$/m.exec(event)?.[1];
  if (!data) return {};
  try {
    const parsed = JSON.parse(data);
    return parsed && typeof parsed === "object" ? parsed as Record<string, unknown> : {};
  } catch {
    return {};
  }
}

function generationDetailDebugState(detail?: ProjectDetailResponse): Record<string, unknown> {
  const preview = detail?.generationProgressDeckPreview;
  return {
    taskStatus: detail?.task.status,
    taskStage: detail?.task.currentStage,
    taskUpdatedAt: detail?.task.updatedAt,
    attemptNo: preview?.attemptNo,
    revision: preview?.revision,
    completedSlides: preview?.completedSlides,
    totalSlides: preview?.totalSlides,
    previewStatus: preview?.status,
    finalRenderId: pickFinalRender(detail)?.id,
  };
}

export function hasActiveStandardGeneration(detail?: ProjectDetailResponse) {
  if (!detail) return false;
  const completedAt = latestCompletedStandardRenderTime(detail);
  if (detail.task.status === "succeeded" && completedAt) return false;
  if (detail.renders.some((render) => isActiveStandardRender(render, completedAt))) return true;
  if (detail.generations.some((generation) => isActiveStandardGenerationRecord(generation, completedAt))) return true;
  if (!["planning", "generating"].includes(detail.task.status)) return false;
  return !hasCompletedStandardRender(detail);
}

function hasCompletedStandardRender(detail: ProjectDetailResponse) {
  return detail.renders.some((render) => render.status === "succeeded" && Boolean(render.outputManifest) && isStandardRender(render));
}

function latestCompletedStandardRenderTime(detail: ProjectDetailResponse) {
  return detail.renders.reduce((latest, render) => {
    if (render.status !== "succeeded" || !render.outputManifest || !isStandardRender(render)) return latest;
    return Math.max(latest, parseTime(render.updatedAt));
  }, 0);
}

function isActiveStandardRender(render: ProjectRender, completedAt: number) {
  if (!["queued", "running"].includes(render.status) || !isStandardRender(render)) return false;
  const updatedAt = parseTime(render.updatedAt);
  return !completedAt || !updatedAt || updatedAt >= completedAt;
}

function isActiveStandardGenerationRecord(generation: ProjectDetailResponse["generations"][number], completedAt: number) {
  if (!["queued", "running"].includes(generation.status) || !isStandardProductCode(generation.productCode)) return false;
  const updatedAt = parseTime(generation.updatedAt);
  return !completedAt || !updatedAt || updatedAt >= completedAt;
}

function isUsableStandardProgressPreview(detail: ProjectDetailResponse | undefined, preview: ProjectProgressDeckPreview | null | undefined) {
  if (!preview?.previewUrl) return false;
  return isProgressPreviewForCurrentAttempt(detail, preview);
}

function isProgressPreviewForCurrentAttempt(detail: ProjectDetailResponse | undefined, preview: ProjectProgressDeckPreview | null | undefined) {
  if (!preview || !detail) return false;
  const currentAttempt = 1 + Number(detail.task.retryCount || 0) + Number(detail.task.planningAutoRetryCount || 0);
  return Number(preview.attemptNo || 0) === currentAttempt;
}

function isStandardRender(render: ProjectRender) {
  return isStandardProductCode(render.productCode);
}

function isStandardProductCode(productCode?: string) {
  return productCode !== ADVANCED_BEAUTIFY_PRODUCT_CODE && productCode !== "image2-slide-revision-preview";
}

function parseTime(value?: string) {
  if (!value) return 0;
  const time = new Date(value).valueOf();
  return Number.isNaN(time) ? 0 : time;
}

function progressHeadline(view: GenerationViewState) {
  if (view.phase === "complete") return "演示文稿已生成完成";
  if (view.phase === "failed") return "本次生成没有完成";
  if (view.phase === "cancelled") return "本次生成已取消";
  if (view.phase === "outline") return "大纲已完成，正在进入页面生成";
  if (view.phase === "rendering") return `正在生成第 ${view.currentSlide} 页`;
  return "正在解析资料与规划内容";
}

function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error(`无法读取图片 ${file.name}`));
    reader.onload = () => typeof reader.result === "string"
      ? resolve(reader.result)
      : reject(new Error(`无法读取图片 ${file.name}`));
    reader.readAsDataURL(file);
  });
}

function formatFileSize(bytes: number): string {
  return bytes >= 1024 * 1024
    ? `${(bytes / 1024 / 1024).toFixed(1)} MB`
    : `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

function progressStatusKicker(view: GenerationViewState) {
  if (view.phase === "complete") return "生成完成";
  if (view.phase === "failed") return "生成中断";
  if (view.phase === "cancelled") return "已取消";
  return "实时生成";
}

function slideProgressText(slide: RailSlide, view: GenerationViewState) {
  if (view.phase === "complete") return "已生成完成";
  if (slide.status === "complete") return "页面内容与预览已完成";
  if (slide.status === "failed") return "生成在此阶段中断";
  if (slide.status === "cancelled") return "任务在此页生成前取消";
  if (slide.status === "waiting" && ["failed", "cancelled"].includes(view.phase)) return "本次未生成";
  if (slide.status === "generating") {
    if (view.phase === "failed") return "生成在此阶段中断";
    if (view.phase === "cancelled") return "取消前等待生成";
    if (view.phase === "outline" || view.phase === "starting") return "等待开始页面生成";
    return "正在生成页面内容";
  }
  return "排队等待生成";
}

function progressHelpText(view: GenerationViewState) {
  if (view.phase === "complete") return "生成完成后可以播放预览或导出高清图片型 PPTX。";
  if (view.phase === "failed") return "生成失败时可以安全重试，已有大纲和中间结果会尽量保留。";
  if (view.phase === "cancelled") return "任务已取消，您可以在当前页面重新生成。";
  return "进度会自动刷新；关闭页面不会影响后台生成。";
}

function signedRenderResourceUrl(render: ProjectRender, url?: string) {
  if (!url || !render.previewUrl) return url || "";
  const token = /\/api\/app\/v2\/render-previews\/([^/]+)\//.exec(render.previewUrl)?.[1];
  const prefix = `/api/app/v2/renders/${render.id}/preview/`;
  if (!token || !url.startsWith(prefix)) return url;
  return `/api/app/v2/render-previews/${token}/${url.slice(prefix.length)}`;
}

function loadSessionDetail(taskId: string): ProjectDetailResponse | undefined {
  try {
    const value = JSON.parse(sessionStorage.getItem(`pptdeep:task:${taskId}`) || "null") as ProjectDetailResponse | undefined;
    return value?.ok ? value : undefined;
  } catch {
    return undefined;
  }
}

function sourceContentPreviewStorageKey(taskId: string) {
  return `pptdeep:source-content-preview:${taskId}`;
}

function loadSourceContentPreview(taskId: string, refKey: string): ProjectSourceContentPreview | undefined {
  try {
    const cached = JSON.parse(sessionStorage.getItem(sourceContentPreviewStorageKey(taskId)) || "null") as {
      refKey?: string;
      preview?: ProjectSourceContentPreview;
    } | null;
    return cached?.refKey === refKey && cached.preview?.schemaVersion === "dfai-source-content-preview/v1"
      ? cached.preview
      : undefined;
  } catch {
    return undefined;
  }
}

function storeSourceContentPreview(taskId: string, refKey: string, preview: ProjectSourceContentPreview) {
  try {
    sessionStorage.setItem(sourceContentPreviewStorageKey(taskId), JSON.stringify({ refKey, preview }));
  } catch {
    // The in-memory copy remains usable when session storage is unavailable or full.
  }
}

function formatShortTime(value?: string) {
  if (!value) return "--:--";
  const date = new Date(value);
  if (Number.isNaN(date.valueOf())) return "--:--";
  return `${String(date.getHours()).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}`;
}

function friendlyError(value: string) {
  if (/timeout|超时/i.test(value)) return "生成服务响应超时，请稍后安全重试";
  if (/model|模型/i.test(value)) return "内容生成服务暂时不可用，请稍后重试";
  if (/worker|租约/i.test(value)) return "生成 Worker 暂时中断，请安全重试";
  return value;
}
